import { useState, useCallback, useMemo, useEffect } from 'react';
import type { WaterParameters, DairyParameters, BreweryParameters, BlendedParameters, SavedSample, AssessmentResult, ProcessSimulation, ProcessStage, ProcessTank, EquipmentItem, DischargeLimits } from '../types/calculator';
import { AVAILABLE_CHEMICALS, EQUIPMENT_LIBRARY, DEFAULT_LIMITS } from '../types/calculator';
import type { StandardCode } from '../types/standards';
import { standardLimitsToCalculator, DEFAULT_STANDARD, getStandard } from '../types/standards';
import { useSession } from '@/context/SessionContext';

// ─── Re-export types for consumers ──────────────────────────────────
export type { WaterParameters, DairyParameters, BreweryParameters, BlendedParameters, SavedSample, AssessmentResult, DischargeLimits };
export { AVAILABLE_CHEMICALS, EQUIPMENT_LIBRARY, DEFAULT_LIMITS };
export type { DosingConfig, DosingChemical, ProcessSimulation, ProcessStage, ProcessTank } from '../types/calculator';

// ─── Classification Helpers (unchanged) ─────────────────────────────────

function classifyBOD(value: number) {
  if (value <= 100) return { level: 'Low', classification: 'Low Organic Load', score: 1 };
  if (value <= 300) return { level: 'Moderate', classification: 'Medium Organic Load', score: 2 };
  return { level: 'High', classification: 'High Organic Load', score: 3 };
}
function classifyCOD(value: number) {
  if (value <= 200) return { level: 'Low', classification: 'Low COD', score: 1 };
  if (value <= 1000) return { level: 'Moderate', classification: 'Medium COD', score: 2 };
  return { level: 'High', classification: 'High COD', score: 3 };
}
function classifyBODCODRatio(ratio: number) {
  if (ratio > 0.5) return { level: 'High', classification: 'Easily Biodegradable', score: 1 };
  if (ratio >= 0.3) return { level: 'Moderate', classification: 'Moderately Biodegradable', score: 2 };
  return { level: 'Low', classification: 'Difficult Biodegradable', score: 3 };
}
function classifyTSS(value: number) {
  if (value <= 100) return { level: 'Low', classification: 'Low Solids', score: 1 };
  if (value <= 350) return { level: 'Moderate', classification: 'Medium Solids', score: 2 };
  return { level: 'High', classification: 'High Solids', score: 3 };
}
function classifyTemperature(value: number) {
  if (value >= 10 && value <= 35) return { level: 'Optimal', classification: 'Optimal for Biology', score: 1 };
  if ((value >= 5 && value < 10) || (value > 35 && value <= 40)) return { level: 'Stress', classification: 'Biological Stress', score: 2 };
  return { level: 'Extreme', classification: 'Biological Inhibition', score: 3 };
}
function classifyOG(value: number) {
  if (value < 15) return { level: 'Low', classification: 'Low Oil Content', score: 1 };
  if (value <= 50) return { level: 'Moderate', classification: 'Oil Separation Advised', score: 2 };
  return { level: 'High', classification: 'Oil Separation Required', score: 3 };
}
function classifyPH(value: number) {
  if (value >= 6.5 && value <= 8.5) return { level: 'Optimal', classification: 'Ideal for Biology', score: 1 };
  if ((value >= 6.0 && value < 6.5) || (value > 8.5 && value <= 9.0)) return { level: 'Marginal', classification: 'pH Adjustment Recommended', score: 2 };
  return { level: 'Extreme', classification: 'Neutralization Required', score: 3 };
}
function classifyTDS(value: number) {
  if (value < 500) return { level: 'Low', classification: 'Fresh Water', score: 1 };
  if (value <= 1500) return { level: 'Moderate', classification: 'Slightly Brackish', score: 2 };
  if (value <= 5000) return { level: 'High', classification: 'Brackish', score: 3 };
  return { level: 'Very High', classification: 'Saline - RO Needed', score: 4 };
}
function classifyConductivity(value: number) {
  if (value < 1500) return { level: 'Low', classification: 'Fresh Water', score: 1 };
  if (value <= 5000) return { level: 'Moderate', classification: 'Brackish', score: 2 };
  if (value <= 30000) return { level: 'High', classification: 'Saline', score: 3 };
  return { level: 'Extreme', classification: 'Hypersaline', score: 4 };
}
function classifyAmmonia(value: number) {
  if (value < 5) return { level: 'Low', classification: 'Low Ammonia', score: 1 };
  if (value <= 25) return { level: 'Moderate', classification: 'Nitrification Recommended', score: 2 };
  return { level: 'High', classification: 'Nitrification Required', score: 3 };
}
function classifyTN(value: number) {
  if (value < 10) return { level: 'Low', classification: 'Low Nitrogen', score: 1 };
  if (value <= 40) return { level: 'Moderate', classification: 'Nutrient Removal Advised', score: 2 };
  return { level: 'High', classification: 'Denitrification Required', score: 3 };
}
function classifyTP(value: number) {
  if (value < 2) return { level: 'Low', classification: 'Low Phosphorus', score: 1 };
  if (value <= 8) return { level: 'Moderate', classification: 'P Removal Advised', score: 2 };
  return { level: 'High', classification: 'P Removal Required', score: 3 };
}
function classifyChlorides(value: number) {
  if (value < 300) return { level: 'Safe', classification: 'No Inhibition', score: 1 };
  if (value <= 1000) return { level: 'Moderate', classification: 'Moderate Inhibition', score: 2 };
  return { level: 'High', classification: 'Toxic to Biomass', score: 3 };
}

// ─── Salinity Calculation ─────────────────────────────────────────────

function calculateSalinityPPT(conductivity: number): number {
  const C = conductivity / 1000;
  const C_std = 42.914;
  const R = C / C_std;
  if (R <= 0) return 0;
  const sqrtR = Math.sqrt(R);
  const salinity = 0.008 + 0.1692 * sqrtR + 25.3851 * R + 14.0941 * Math.pow(R, 1.5) - 7.0261 * Math.pow(R, 2) + 2.7081 * Math.pow(R, 2.5);
  return Math.max(0, salinity);
}

function calculateSalineProbability(conductivity: number) {
  const gaussian = (x: number, mu: number, sigma: number) =>
    Math.exp(-0.5 * Math.pow((x - mu) / sigma, 2));
  const freshMembership = gaussian(conductivity, 500, 600);
  const brackishMembership = gaussian(conductivity, 3000, 1500);
  const salineMembership = gaussian(conductivity, 15000, 8000);
  const hypersalineMembership = gaussian(conductivity, 50000, 20000);
  const total = freshMembership + brackishMembership + salineMembership + hypersalineMembership;
  const freshProb = total > 0 ? (freshMembership / total) * 100 : 0;
  const brackishProb = total > 0 ? (brackishMembership / total) * 100 : 0;
  const salineProb = total > 0 ? (salineMembership / total) * 100 : 0;
  const hypersalineProb = total > 0 ? (hypersalineMembership / total) * 100 : 0;
  const salineExistsProb = brackishProb + salineProb + hypersalineProb;
  let label: string;
  let confidence: string;
  if (conductivity < 1500) { label = 'Fresh Water'; confidence = 'High'; }
  else if (conductivity < 5000) { label = 'Brackish Water'; confidence = 'High'; }
  else if (conductivity < 30000) { label = 'Saline Water'; confidence = conductivity > 10000 ? 'Very High' : 'High'; }
  else { label = 'Hypersaline Water'; confidence = 'Very High'; }
  return {
    probability: Math.round(salineExistsProb), label, confidence,
    freshProb: Math.round(freshProb), brackishProb: Math.round(brackishProb),
    salineProb: Math.round(salineProb), hypersalineProb: Math.round(hypersalineProb),
  };
}

// ─── Default Parameters ─────────────────────────────────────────────

const defaultParams: WaterParameters = {
  bod5: 20500, cod: 45000, tss: 10900, turbidity: 450, temperature: 25, color: 150,
  oilGrease: 560, freeOil: 280, ph: 7.21, tds: 10700, alkalinity: 200, chlorides: 6500,
  ammoniaN: 850, totalNitrogen: 1150, totalPhosphorus: 128, tkn: 1150,
  lead: 0.05, mercury: 0.002, cadmium: 0.02, chromium: 0.1, arsenic: 0.03,
  nickel: 0.2, zinc: 0.5, copper: 0.3, sulfides: 15, phenol: 12, surfactants: 25,
  flowRate: 500, conductivity: 17600,
};

const defaultDairy: DairyParameters = {
  enabled: false,
  flowRate: 100,
  bod5: 2500,
  cod: 4500,
  tss: 1200,
  oilGrease: 180,
  ph: 7.5,
  tds: 1200,
  totalNitrogen: 85,
  totalPhosphorus: 18,
  temperature: 30,
  fatContent: 120,
  lactose: 450,
};

const defaultBrewery: BreweryParameters = {
  enabled: false,
  flowRate: 150,
  bod5: 8500,
  cod: 13500,
  tss: 2800,
  ph: 5.2,
  tds: 2500,
  totalNitrogen: 120,
  totalPhosphorus: 35,
  temperature: 22,
  alcoholContent: 0.5,
  hopsResidue: 65,
  yeastCount: 850,
};

// ─── Blending Function ──────────────────────────────────────────────

function blendParameters(
  base: WaterParameters,
  dairy: DairyParameters | null,
  brewery: BreweryParameters | null
): BlendedParameters {
  const totalFlow = base.flowRate +
    (dairy?.enabled ? dairy.flowRate : 0) +
    (brewery?.enabled ? brewery.flowRate : 0);

  const contributions: { name: string; flowRate: number; percentage: number }[] = [
    { name: 'Base Wastewater', flowRate: base.flowRate, percentage: Math.round((base.flowRate / totalFlow) * 100) },
  ];
  if (dairy?.enabled) {
    contributions.push({ name: 'Dairy Effluent', flowRate: dairy.flowRate, percentage: Math.round((dairy.flowRate / totalFlow) * 100) });
  }
  if (brewery?.enabled) {
    contributions.push({ name: 'Brewery Effluent', flowRate: brewery.flowRate, percentage: Math.round((brewery.flowRate / totalFlow) * 100) });
  }

  const wBase = base.flowRate / totalFlow;
  const wDairy = dairy?.enabled ? dairy.flowRate / totalFlow : 0;
  const wBrewery = brewery?.enabled ? brewery.flowRate / totalFlow : 0;

  const blend = (baseVal: number, dairyVal: number, breweryVal: number) =>
    Number((baseVal * wBase + dairyVal * wDairy + breweryVal * wBrewery).toFixed(2));

  const blended: WaterParameters = {
    bod5: blend(base.bod5, dairy?.bod5 ?? 0, brewery?.bod5 ?? 0),
    cod: blend(base.cod, dairy?.cod ?? 0, brewery?.cod ?? 0),
    tss: blend(base.tss, dairy?.tss ?? 0, brewery?.tss ?? 0),
    turbidity: blend(base.turbidity, dairy?.tss ?? 0 * 0.4, brewery?.tss ?? 0 * 0.35),
    temperature: blend(base.temperature, dairy?.temperature ?? 0, brewery?.temperature ?? 0),
    color: blend(base.color, 80, 120),
    oilGrease: blend(base.oilGrease, dairy?.oilGrease ?? 0, 15),
    freeOil: blend(base.freeOil, dairy?.oilGrease ?? 0 * 0.5, 8),
    ph: Number((base.ph * wBase + (dairy?.ph ?? 7) * wDairy + (brewery?.ph ?? 7) * wBrewery).toFixed(2)),
    tds: blend(base.tds, dairy?.tds ?? 0, brewery?.tds ?? 0),
    alkalinity: blend(base.alkalinity, 250, 80),
    chlorides: blend(base.chlorides, 180, 120),
    ammoniaN: blend(base.ammoniaN, dairy?.totalNitrogen ?? 0 * 0.7, brewery?.totalNitrogen ?? 0 * 0.6),
    totalNitrogen: blend(base.totalNitrogen, dairy?.totalNitrogen ?? 0, brewery?.totalNitrogen ?? 0),
    totalPhosphorus: blend(base.totalPhosphorus, dairy?.totalPhosphorus ?? 0, brewery?.totalPhosphorus ?? 0),
    tkn: blend(base.tkn, dairy?.totalNitrogen ?? 0, brewery?.totalNitrogen ?? 0 * 0.9),
    lead: base.lead,
    mercury: base.mercury,
    cadmium: base.cadmium,
    chromium: base.chromium,
    arsenic: base.arsenic,
    nickel: base.nickel,
    zinc: base.zinc,
    copper: base.copper,
    sulfides: base.sulfides,
    phenol: blend(base.phenol, 2, brewery?.hopsResidue ?? 0 * 0.3),
    surfactants: base.surfactants,
    flowRate: totalFlow,
    conductivity: blend(base.conductivity, dairy?.tds ?? 0 * 1.5, brewery?.tds ?? 0 * 2.0),
  };

  let label: string;
  let source: 'base' | 'dairy' | 'brewery' | 'combined';
  if (dairy?.enabled && brewery?.enabled) { label = 'Combined Base + Dairy + Brewery'; source = 'combined'; }
  else if (dairy?.enabled) { label = 'Base + Dairy Effluent'; source = 'dairy'; }
  else if (brewery?.enabled) { label = 'Base + Brewery Effluent'; source = 'brewery'; }
  else { label = 'Base Wastewater Only'; source = 'base'; }

  return { source, label, params: blended, contributions };
}

// ─── Sample Storage ─────────────────────────────────────────────────

const SAMPLES_KEY = 'rb_water_samples_v2';

function loadSamples(): SavedSample[] {
  try {
    const raw = localStorage.getItem(SAMPLES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveSamples(samples: SavedSample[]) {
  localStorage.setItem(SAMPLES_KEY, JSON.stringify(samples));
}

function calculateLamellaSettlement(
  eq: import('../types/calculator').EquipmentItem,
  flowRateM3PerDay: number,
  inletTss: number,
  inletBod5: number
): import('../types/calculator').LamellaSettlementCalc | undefined {
  if (!eq.lamellaConfig) return undefined;
  const cfg = eq.lamellaConfig;
  const flowM3PerHr = flowRateM3PerDay / 24;

  // Required settling area based on surface loading
  const requiredAreaM2 = flowM3PerHr / cfg.maxSurfaceLoading;
  // Projected area with inclined plates (8-10x multiplier)
  const projectedAreaM2 = requiredAreaM2 * cfg.projectedAreaFactor;
  // Assume a rectangular tank footprint for the lamella module
  const actualTankAreaM2 = requiredAreaM2 * 1.3; // 30% extra for inlet/outlet zones

  const surfaceLoading = flowM3PerHr / actualTankAreaM2;

  // Estimate plate count based on area and spacing
  const plateWidth = Math.sqrt(actualTankAreaM2); // assume square-ish footprint
  const plateCount = Math.round((plateWidth * 1000) / cfg.plateSpacing);

  // HRT in minutes
  const hrtMin = (actualTankAreaM2 * 2.0) / flowM3PerHr * 60; // 2m effective depth

  let compliance: 'Pass' | 'Marginal' | 'Fail' = 'Pass';
  if (surfaceLoading > cfg.maxSurfaceLoading * 1.1) compliance = 'Fail';
  else if (surfaceLoading > cfg.maxSurfaceLoading * 0.9) compliance = 'Marginal';

  return {
    equipmentId: eq.id,
    flowRateM3PerDay: flowRateM3PerDay,
    flowRateM3PerHour: Number(flowM3PerHr.toFixed(1)),
    requiredSettlingAreaM2: Number(requiredAreaM2.toFixed(1)),
    projectedSettlingAreaM2: Number(projectedAreaM2.toFixed(1)),
    actualTankAreaM2: Number(actualTankAreaM2.toFixed(1)),
    surfaceLoadingRateMPerHr: Number(surfaceLoading.toFixed(2)),
    plateCount,
    plateSpacingMm: cfg.plateSpacing,
    plateAngleDegrees: cfg.plateAngle,
    hydraulicRetentionTimeMin: Number(hrtMin.toFixed(1)),
    estimatedTSSRemovalPercent: cfg.typicalRemovalRate,
    estimatedBODRemovalPercent: 30, // BOD removal is lower than TSS in lamella
    compliance,
    notes: `Lamella plates at ${cfg.plateAngle}° with ${cfg.plateSpacing}mm spacing. Projected settling area ${cfg.projectedAreaFactor}x actual footprint. ${compliance === 'Pass' ? 'Surface loading within design limits.' : compliance === 'Marginal' ? 'Surface loading approaching limit — consider larger unit or dual train.' : 'Surface loading exceeds design — larger unit or multiple trains required.'}`,
  };
}

function generateEquipmentDosingRequirements(
  stages: import('../types/calculator').ProcessStage[],
  p: import('../types/calculator').WaterParameters
): import('../types/calculator').EquipmentDosingReq[] {
  const reqs: import('../types/calculator').EquipmentDosingReq[] = [];

  for (const stage of stages) {
    for (const eq of stage.equipment) {
      let requiresCoagulant = false;
      let requiresFlocculant = false;
      let requiresPhAdjustment = false;
      const recommendedChemicals: { chemicalId: string; name: string; formula: string; reason: string; recommendedDose: number; doseUnit: string }[] = [];
      let severity: 'required' | 'recommended' | 'optional' = 'optional';
      let reason = '';

      // DAF always needs coagulant for good performance
      if (eq.id.includes('daf')) {
        requiresCoagulant = true;
        requiresFlocculant = true;
        severity = p.oilGrease > 50 || p.tss > 500 ? 'required' : 'recommended';
        reason = `DAF performance depends on chemical conditioning. ${p.oilGrease > 50 ? 'High O&G requires strong coagulant.' : ''}`;
        recommendedChemicals.push(
          { chemicalId: 'fecl3', name: 'Ferric Chloride', formula: 'FeCl3', reason: 'Strong coagulant, excellent for O&G and TSS. Lowers pH — monitor if pH < 6.5.', recommendedDose: p.oilGrease > 100 ? 300 : p.oilGrease > 50 ? 200 : 150, doseUnit: 'mg/L' },
          { chemicalId: 'alum', name: 'Aluminium Sulfate', formula: 'Al2(SO4)3', reason: 'Economical alternative to ferric. Good for moderate turbidity.', recommendedDose: p.oilGrease > 100 ? 250 : p.oilGrease > 50 ? 180 : 120, doseUnit: 'mg/L' },
          { chemicalId: 'pac', name: 'Polyaluminium Chloride', formula: 'PAC', reason: 'Lower dose than alum, less pH impact. Good for variable flows.', recommendedDose: p.oilGrease > 100 ? 200 : p.oilGrease > 50 ? 140 : 100, doseUnit: 'mg/L' },
          { chemicalId: 'feso4', name: 'Ferrous Sulfate', formula: 'FeSO4', reason: 'Economical iron salt. Less pH impact than ferric chloride.', recommendedDose: p.oilGrease > 100 ? 280 : p.oilGrease > 50 ? 190 : 130, doseUnit: 'mg/L' },
        );
      }

      // Lamella Clarifier benefits from coagulant for colloidal solids
      if (eq.id === 'lamella_clarifier') {
        if (p.tss > 150 || p.turbidity > 100) {
          requiresCoagulant = true;
          severity = p.tss > 300 ? 'required' : 'recommended';
          reason = `Lamella settling is enhanced with coagulation for ${p.tss > 300 ? 'high' : 'moderate'} TSS levels.`;
          recommendedChemicals.push(
            { chemicalId: 'fecl3', name: 'Ferric Chloride', formula: 'FeCl3', reason: 'Strong coagulant for rapid settling in lamella plates.', recommendedDose: p.tss > 300 ? 200 : 120, doseUnit: 'mg/L' },
            { chemicalId: 'alum', name: 'Aluminium Sulfate', formula: 'Al2(SO4)3', reason: 'Standard coagulant, cost-effective for lamella clarifiers.', recommendedDose: p.tss > 300 ? 180 : 100, doseUnit: 'mg/L' },
            { chemicalId: 'pac', name: 'Polyaluminium Chloride', formula: 'PAC', reason: 'Pre-polymerized — faster floc formation in short HRT lamella.', recommendedDose: p.tss > 300 ? 150 : 80, doseUnit: 'mg/L' },
          );
        }
        // Flocculant aid for fine particles
        if (p.tss > 200) {
          requiresFlocculant = true;
          recommendedChemicals.push(
            { chemicalId: 'polymer', name: 'Polymer Flocculant', formula: 'Polyacrylamide', reason: 'Flocculation aid improves settling velocity in lamella plates.', recommendedDose: 2.5, doseUnit: 'mg/L' },
          );
        }
      }

      // Coagulation basin always needs chemicals
      if (eq.id === 'coag_basin') {
        requiresCoagulant = true;
        requiresFlocculant = true;
        severity = 'required';
        reason = 'Coagulation/flocculation basin requires chemical dosing to function.';
        recommendedChemicals.push(
          { chemicalId: 'fecl3', name: 'Ferric Chloride', formula: 'FeCl3', reason: 'Primary coagulant. Strong phosphorus removal.', recommendedDose: p.totalPhosphorus > 10 ? 250 : 150, doseUnit: 'mg/L' },
          { chemicalId: 'alum', name: 'Aluminium Sulfate', formula: 'Al2(SO4)3', reason: 'Standard coagulant. Good turbidity removal.', recommendedDose: p.totalPhosphorus > 10 ? 200 : 120, doseUnit: 'mg/L' },
          { chemicalId: 'pac', name: 'Polyaluminium Chloride', formula: 'PAC', reason: 'Lower dose, wider pH range.', recommendedDose: p.totalPhosphorus > 10 ? 160 : 90, doseUnit: 'mg/L' },
          { chemicalId: 'polymer', name: 'Polymer Flocculant', formula: 'Polyacrylamide', reason: 'Flocculation aid for improved settling.', recommendedDose: 2, doseUnit: 'mg/L' },
        );
      }

      // Electrocoagulation doesn't need external chemicals (in-situ generation)
      if (eq.id === 'electrocoagulation') {
        severity = 'optional';
        reason = 'Electrocoagulation generates coagulant in-situ via sacrificial anodes. No external chemicals required, but polymer may enhance settling.';
        if (p.tss > 200) {
          recommendedChemicals.push(
            { chemicalId: 'polymer', name: 'Polymer Flocculant', formula: 'Polyacrylamide', reason: 'Optional flocculation aid for electrocoagulation sludge.', recommendedDose: 1.5, doseUnit: 'mg/L' },
          );
        }
      }

      // pH adjustment needed for coagulant effectiveness
      if (requiresCoagulant && (p.ph < 6.0 || p.ph > 8.5)) {
        requiresPhAdjustment = true;
        if (p.ph < 6.0) {
          recommendedChemicals.push(
            { chemicalId: 'lime', name: 'Hydrated Lime', formula: 'Ca(OH)2', reason: 'pH buffer for acidic wastewater. Required for alum/PAC effectiveness.', recommendedDose: (6.5 - p.ph) * 100, doseUnit: 'mg/L' },
          );
        }
        if (p.ph > 8.5) {
          recommendedChemicals.push(
            { chemicalId: 'caustic', name: 'Caustic Soda', formula: 'NaOH', reason: 'Not needed — pH already high. Consider acid if ferric is used (ferric lowers pH).', recommendedDose: 0, doseUnit: 'mg/L' },
          );
        }
      }

      if (recommendedChemicals.length > 0) {
        reqs.push({
          equipmentName: eq.name,
          equipmentId: eq.id,
          requiresCoagulant,
          requiresFlocculant,
          requiresPhAdjustment,
          recommendedChemicals,
          severity,
          reason,
        });
      }
    }
  }

  return reqs;
}

// ─── Main Hook ──────────────────────────────────────────────────────

export function useCalculator() {
  const { activeStandard: contextStandard, setActiveStandard: setContextStandard } = useSession();

  const [params, setParams] = useState<WaterParameters>(defaultParams);
  const [dairy, setDairy] = useState<DairyParameters>(defaultDairy);
  const [brewery, setBrewery] = useState<BreweryParameters>(defaultBrewery);
  const [activeStandard, setActiveStandard] = useState<StandardCode>(contextStandard);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [samples, setSamples] = useState<SavedSample[]>(loadSamples);
  const [activeSampleId, setActiveSampleId] = useState<string | null>(null);
  const [dosingConfig, setDosingConfig] = useState<import('../types/calculator').DosingConfig[]>([
    { enabled: false, chemicalId: 'fecl3', dose: 150 },
    { enabled: false, chemicalId: 'alum', dose: 100 },
    { enabled: false, chemicalId: 'polymer', dose: 2 },
    { enabled: false, chemicalId: 'lime', dose: 100 },
  ]);
  const [limits, setLimits] = useState<DischargeLimits>(() => {
    const std = getStandard(contextStandard);
    return standardLimitsToCalculator(std);
  });

  // Sync with SessionContext standard changes (e.g. from StandardsPage)
  useEffect(() => {
    if (contextStandard !== activeStandard) {
      setActiveStandard(contextStandard);
      const std = getStandard(contextStandard);
      setLimits(standardLimitsToCalculator(std));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextStandard]);

  useEffect(() => { saveSamples(samples); }, [samples]);

  const updateParam = useCallback(<K extends keyof WaterParameters>(key: K, value: number) => {
    setParams(prev => ({ ...prev, [key]: value }));
  }, []);

  const updateDairy = useCallback(<K extends keyof DairyParameters>(key: K, value: DairyParameters[K]) => {
    setDairy(prev => ({ ...prev, [key]: value }));
  }, []);

  const updateBrewery = useCallback(<K extends keyof BreweryParameters>(key: K, value: BreweryParameters[K]) => {
    setBrewery(prev => ({ ...prev, [key]: value }));
  }, []);

  const updateStandard = useCallback((code: StandardCode) => {
    setActiveStandard(code);
    setContextStandard(code);
    // Apply the new standard's limits automatically
    const std = getStandard(code);
    setLimits(standardLimitsToCalculator(std));
  }, [setContextStandard]);

  const updateDosing = useCallback((index: number, updates: Partial<import('../types/calculator').DosingConfig>) => {
    setDosingConfig(prev => prev.map((d, i) => i === index ? { ...d, ...updates } : d));
  }, []);

  const addDosing = useCallback(() => {
    setDosingConfig(prev => [...prev, { enabled: false, chemicalId: 'fecl3', dose: 100 }]);
  }, []);

  const addSpecificDosing = useCallback((chemicalId: string, dose: number) => {
    setDosingConfig(prev => [...prev, { enabled: true, chemicalId, dose }]);
  }, []);

  const removeDosing = useCallback((index: number) => {
    setDosingConfig(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateLimit = useCallback(<K extends keyof DischargeLimits>(key: K, value: number) => {
    setLimits(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetLimits = useCallback(() => {
    setLimits({ ...DEFAULT_LIMITS });
  }, []);

  const resetParams = useCallback(() => {
    setParams(defaultParams);
    setDairy(defaultDairy);
    setBrewery(defaultBrewery);
    setDosingConfig([
      { enabled: false, chemicalId: 'fecl3', dose: 150 },
      { enabled: false, chemicalId: 'alum', dose: 100 },
      { enabled: false, chemicalId: 'polymer', dose: 2 },
      { enabled: false, chemicalId: 'lime', dose: 100 },
    ]);
    setHasCalculated(false);
    setActiveSampleId(null);
  }, []);

  const runAssessment = useCallback(() => {
    setHasCalculated(true);
    // Persist assessment run to sessionStorage with timestamp and params
    try {
      const sessionData = {
        timestamp: new Date().toISOString(),
        params: { ...params },
        dairy: { ...dairy },
        brewery: { ...brewery },
        dosingConfig: [...dosingConfig],
        activeStandard,
      };
      sessionStorage.setItem('rb_last_assessment', JSON.stringify(sessionData));
      // Clear any old "dosing acknowledged" flag so fresh assessment triggers warning
      sessionStorage.removeItem('rb_dosing_acknowledged');
    } catch { /* ignore */ }
  }, [params, dairy, brewery, dosingConfig, activeStandard]);

  // ─── Blended Parameters ─────────────────────────────────────────
  const blended = useMemo(() => {
    return blendParameters(
      params,
      dairy.enabled ? dairy : null,
      brewery.enabled ? brewery : null
    );
  }, [params, dairy, brewery]);

  // ─── Process Simulator ────────────────────────────────────────
  const simulation = useMemo<ProcessSimulation | undefined>(() => {
    if (!hasCalculated) return undefined;

    const p = blended.params;
    const activeDosings = dosingConfig.filter(d => d.enabled);
    const chemLookup = (id: string) => AVAILABLE_CHEMICALS.find(c => c.id === id)!;

    const activeChemicals = activeDosings.map(d => ({ chem: chemLookup(d.chemicalId), dose: d.dose }));

    let totalPhChange = 0;
    for (const { chem, dose } of activeChemicals) {
      totalPhChange += chem.phChangePerMgL * dose;
    }

    let current: WaterParameters = { ...p };
    const stages: ProcessStage[] = [];
    let cumulativeBodRemoved = 0;
    let cumulativeCodRemoved = 0;
    let cumulativeTssRemoved = 0;

    const makeTanks = (equipmentList: EquipmentItem[]): ProcessTank[] =>
      equipmentList.map((eq, idx) => {
        const volumeM3 = eq.typicalHRT > 0.05
          ? Math.round((p.flowRate / 24) * eq.typicalHRT)
          : Math.max(2, Math.round(p.flowRate / 24 * 0.1));
        const depth = eq.category === 'Tertiary' || eq.category === 'Polishing' || eq.category === 'Disinfection' ? 2.5 : 3.0;
        const diameter = Math.round(Math.sqrt(volumeM3 / 3.1416 / depth) * 2);
        const dimensions = volumeM3 > 50
          ? `Ø${diameter} × ${depth} m (cylindrical)`
          : `≈ ${Math.round(Math.cbrt(volumeM3 * 2))}×${Math.round(Math.cbrt(volumeM3 * 2))}×${Math.round(volumeM3 / (Math.pow(Math.cbrt(volumeM3 * 2), 2)))} m`;
        return {
          id: `${eq.id}_${idx}`,
          name: eq.name,
          type: eq.type,
          purpose: eq.purpose,
          volumeM3,
          dimensions,
          hrt: eq.typicalHRT,
          notes: eq.notes,
        };
      });

    const applyStage = (
      stageNumber: number,
      stageName: string,
      equipmentList: EquipmentItem[],
      chemicals: { name: string; dose: number; unit: string }[]
    ): void => {
      let bodRed = 0;
      let codRed = 0;
      let tssRed = 0;
      let ogRed = 0;
      let tnRed = 0;
      let tpRed = 0;
      let tdsMultiplier = 1;
      let phChange = 0;

      // Issue fix: if user has saved a DAF design in DAFSpecPage, read the physics-based
      // removal fractions from localStorage ('daf-size-v2') instead of fixed library defaults.
      // This ensures the process train reflects the actual designed DAF performance.
      let dafPhysicsSize: { tssRemoval: number; ogRemoval: number; bodRemoval: number; codRemoval: number } | null = null;
      try {
        const raw = typeof window !== 'undefined' ? window.localStorage.getItem('daf-size-v2') : null;
        if (raw) dafPhysicsSize = JSON.parse(raw);
      } catch { /* ignore */ }

      for (const eq of equipmentList) {
        const isDaf = eq.id.includes('daf');
        const tssR = isDaf && dafPhysicsSize ? dafPhysicsSize.tssRemoval : eq.tssReduction;
        const ogR  = isDaf && dafPhysicsSize ? dafPhysicsSize.ogRemoval  : eq.ogReduction;
        const bodR = isDaf && dafPhysicsSize ? dafPhysicsSize.bodRemoval : eq.bodReduction;
        const codR = isDaf && dafPhysicsSize ? dafPhysicsSize.codRemoval : eq.codReduction;
        bodRed = 1 - (1 - bodRed) * (1 - bodR);
        codRed = 1 - (1 - codRed) * (1 - codR);
        tssRed = 1 - (1 - tssRed) * (1 - tssR);
        ogRed  = 1 - (1 - ogRed)  * (1 - ogR);
        tnRed = 1 - (1 - tnRed) * (1 - eq.tnReduction);
        tpRed = 1 - (1 - tpRed) * (1 - eq.tpReduction);
        tdsMultiplier *= (1 + eq.tdsChange);
        phChange += eq.phChange;
      }

      const isChemStage = stageName === 'Primary' || stageName === 'Pre-Treatment';
      const stagePhChange = isChemStage ? totalPhChange * (stageName === 'Primary' ? 0.7 : 0.3) : 0;

      if (isChemStage && activeChemicals.length > 0) {
        const chemBoost = activeChemicals.some(ac => ['fecl3','alum','pac','feso4'].includes(ac.chem.id)) ? 0.05 : 0;
        bodRed = Math.min(0.98, bodRed + chemBoost);
        tssRed = Math.min(0.98, tssRed + chemBoost);
      }

      const newBod = Math.max(5, current.bod5 * (1 - bodRed));
      const newCod = Math.max(10, current.cod * (1 - codRed));
      const newTss = Math.max(2, current.tss * (1 - tssRed));
      const newOg = Math.max(1, current.oilGrease * (1 - ogRed));
      const newPh = Math.max(4.0, Math.min(12.0, current.ph + phChange + stagePhChange));
      const newTn = Math.max(1, current.totalNitrogen * (1 - tnRed));
      const newTkn = newTn * 0.85; // TKN is typically ~85% of TN
      const newTp = Math.max(0.1, current.totalPhosphorus * (1 - tpRed));
      const newTds = Math.max(50, current.tds * tdsMultiplier);
      const newCond = Math.max(50, current.conductivity * tdsMultiplier);
      const newTurbidity = Math.max(1, current.turbidity * (1 - tssRed * 0.8));

      cumulativeBodRemoved = 1 - (newBod / p.bod5);
      cumulativeCodRemoved = 1 - (newCod / p.cod);
      cumulativeTssRemoved = 1 - (newTss / p.tss);

      const tanks = makeTanks(equipmentList);
      const cumulativeVolumeM3 = tanks.reduce((sum, t) => sum + t.volumeM3, 0);

      // ─── DAF Sludge Calculation ─────────────────────────────
      let sludgeGeneration: import('../types/calculator').ProcessStage['sludgeGeneration'] = undefined;
      const hasDaf = equipmentList.some(e => e.id.includes('daf'));
      if (hasDaf) {
        const tssRemoved = current.tss - newTss;
        const ogRemoved = current.oilGrease - newOg;
        const totalMassRemoved = tssRemoved + ogRemoved * 0.8; // O&G contributes ~80% of TSS mass
        const chemFactor = activeChemicals.length > 0 ? 1.5 : 1.0; // Chemicals increase sludge by 50%
        const sludgeVolumePercent = (totalMassRemoved / 10000) * chemFactor * 0.8; // 0.3-2% of flow
        const sludgeVolumeM3PerDay = Math.round((p.flowRate * Math.min(0.025, Math.max(0.003, sludgeVolumePercent))) * 10) / 10;
        const drySolidsKgPerDay = Math.round(totalMassRemoved * p.flowRate / 1000 * 1.2 * chemFactor);
        sludgeGeneration = {
          volumeM3PerDay: sludgeVolumeM3PerDay,
          drySolidsKgPerDay: drySolidsKgPerDay,
          moisturePercent: 95,
          description: `DAF floated sludge: ${tssRemoved.toFixed(0)} mg/L TSS + ${ogRemoved.toFixed(0)} mg/L O&G removed. With ${activeChemicals.length > 0 ? 'chemical coagulation (metal hydroxide sludge)' : 'no chemicals (natural flotation)'}.`,
        };
      }

      stages.push({
        stageNumber,
        stageName,
        equipment: equipmentList,
        flowRate: p.flowRate,
        ph: Number(newPh.toFixed(2)),
        bod5: Number(newBod.toFixed(1)),
        cod: Number(newCod.toFixed(1)),
        tss: Number(newTss.toFixed(1)),
        tds: Number(newTds.toFixed(0)),
        oilGrease: Number(newOg.toFixed(1)),
        totalNitrogen: Number(newTn.toFixed(1)),
        tkn: Number(newTkn.toFixed(1)),
        totalPhosphorus: Number(newTp.toFixed(1)),
        conductivity: Number(newCond.toFixed(0)),
        temperature: current.temperature,
        bodReductionSoFar: Number(cumulativeBodRemoved.toFixed(3)),
        codReductionSoFar: Number(cumulativeCodRemoved.toFixed(3)),
        tssReductionSoFar: Number(cumulativeTssRemoved.toFixed(3)),
        cumulativeVolumeM3,
        chemicals,
        tanks,
        sludgeGeneration,
      });

      current = { ...current, bod5: newBod, cod: newCod, tss: newTss, oilGrease: newOg, ph: newPh, totalNitrogen: newTn, totalPhosphorus: newTp, tds: newTds, conductivity: newCond, turbidity: newTurbidity };
    };

    const isSaline = p.conductivity > 5000 || p.tds > 5000;
    const findEq = (id: string) => EQUIPMENT_LIBRARY.find(e => e.id === id)!;
    const compatible = (eq: EquipmentItem) => !isSaline || eq.salineCompatible;

    // Pre-Treatment
    const preTreatment: EquipmentItem[] = [
      findEq('eq_tank'),
      findEq('screen_fine'),
      ...(p.flowRate > 200 ? [findEq(p.flowRate > 1000 ? 'grit_aerated' : 'grit_vortex')] : []),
      findEq('screening_washer'),
    ].filter(compatible);

    // Buffer tank for all flows (flow balancing is always beneficial)
    preTreatment.push(findEq('buffer_tank'));

    // Emergency tank for larger flows or high-risk sites
    if (p.flowRate > 500 || p.bod5 > 1000 || p.oilGrease > 50) {
      preTreatment.push(findEq('emergency_tank'));
    }

    // Aerated Equalization: when high BOD, dairy, brewery, or odor-risk
    const needsAeratedEQ = p.bod5 > 500 || dairy?.enabled || brewery?.enabled || p.sulfides > 2 || p.ph < 5.5;
    if (needsAeratedEQ) {
      preTreatment.push(findEq('aerated_equalization'));
    }
    applyStage(1, 'Pre-Treatment', preTreatment.filter(compatible), []);

    // ─── CPI Oil Separator (mid-range oil, before DAF or instead of DAF) ───
    // When oil is moderate (5-50 mg/L) but not high enough for full DAF
    const useCpi = p.oilGrease > 5 && p.oilGrease <= 50 && p.freeOil <= 50;
    if (useCpi) {
      applyStage(1.5, 'Oil Separation', [findEq('cpi_separator')].filter(compatible), []);
    }

    // Primary
    const primary: EquipmentItem[] = [];
    const needsDaf = p.oilGrease > 15 || p.freeOil > 50;
    const isHighBodSaline = isSaline && p.bod5 > 1000;
    const isVeryHighCod = p.cod > 10000;

    // Electrocoagulation for saline high-BOD/brewery streams
    if (isHighBodSaline) {
      primary.push(findEq('electrocoagulation'));
    }
    // Wet Air Oxidation for extremely high COD
    if (isVeryHighCod) {
      primary.push(findEq('wet_air_oxidation'));
    }

    if (needsDaf) {
      primary.push(findEq(p.flowRate <= 20000 ? 'daf_rect' : 'daf_circular'));
    }
    const hasCoagulant = activeChemicals.some(ac => ['fecl3','alum','pac','feso4'].includes(ac.chem.id));
    if (hasCoagulant && !needsDaf && !isHighBodSaline) {
      primary.push(findEq('coag_basin'));
    }
    // Primary solids separation
    if (p.tss > 200 && !needsDaf) {
      primary.push(findEq('sedimentation_pri'));
    }
    if (p.tss > 100 && p.flowRate <= 50000 && !needsDaf) {
      primary.push(findEq('lamella_clarifier'));
    }
    // Extreme TSS after DAF: lamella backup
    if (needsDaf && p.tss > 500 && p.flowRate <= 50000) {
      primary.push(findEq('lamella_clarifier'));
    }
    if (p.oilGrease > 15 && !needsDaf) {
      primary.push(findEq('api_sep'));
    }
    const dafChemicals = activeChemicals.map(ac => ({
      name: ac.chem.name, dose: ac.dose, unit: ac.chem.doseUnit
    }));
    // Add chemical storage/dosing station when any chemicals are active
    if (activeChemicals.length > 0) {
      primary.push(findEq('chemical_storage_tank'));
    }
    if (primary.length > 0) {
      applyStage(2, 'Primary', primary.filter(compatible), dafChemicals);
    }

    // ─── Mid-Stage pH Adjustment ───
    // After primary, pH may have shifted from coagulants or acid/alkaline influent
    // Adjust before biological treatment which needs pH 6.5-8.5
    const phAfterPrimary = current.ph;
    const needsMidPhAdjust = phAfterPrimary < 6.0 || phAfterPrimary > 9.0;
    if (needsMidPhAdjust) {
      applyStage(2.5, 'pH Adjustment', [findEq('ph_adjust_tank')].filter(compatible), []);
    }

    // Secondary
    const secondary: EquipmentItem[] = [];

    // Aeration basin needed when biological treatment is selected
    const needsBiologicalAeration = !isSaline && current.bod5 > 50 && p.flowRate >= 100;
    if (needsBiologicalAeration) {
      secondary.push(findEq('aeration_basin'));
    }

    if (!isSaline && current.bod5 > 50) {
      // Standard biological for non-saline
      if (p.bod5 > 1000 && p.temperature > 15) {
        secondary.push(findEq('uasb'));
      }
      if (p.bod5 > 300) {
        if (p.flowRate <= 50000 && p.tss < 200) {
          secondary.push(findEq('mbr'));
        } else if (p.flowRate <= 50000) {
          secondary.push(findEq('sbr'));
        } else {
          secondary.push(findEq('activated_sludge'));
        }
      } else if (p.flowRate < 5000) {
        secondary.push(findEq('extended_aeration'));
      }
      if (p.flowRate < 2000 && p.bod5 < 300 && p.tds < 1500) {
        secondary.push(findEq('constructed_wetland'));
      }
    } else if (isSaline && current.bod5 > 100) {
      // SALINE biological
      if (p.bod5 > 1500 && brewery?.enabled) {
        secondary.push(findEq('abr'));
      }
      if (p.bod5 > 500) {
        secondary.push(findEq('halophilic_mbbr'));
      }
      if (p.bod5 > 200 || p.flowRate > 500) {
        secondary.push(findEq('saf'));
      }
      if (p.bod5 > 2000 && p.temperature > 20 && p.conductivity < 15000) {
        secondary.push(findEq('uasb'));
      }
    }
    if (secondary.length > 0) {
      applyStage(3, 'Secondary', secondary.filter(compatible), []);
    }

    // Tertiary
    const tertiary: EquipmentItem[] = [];
    if (current.tss > 50) {
      if (isSaline || p.flowRate > 500) {
        tertiary.push(findEq('mmf_pressure'));
      } else {
        tertiary.push(findEq('mmf_gravity'));
      }
      if (current.tss > 200 && p.flowRate < 10000) {
        tertiary.push(findEq('cloth_filter'));
      }
    }
    if (p.phenol > 5 || current.color > 50 || (current.bod5 / current.cod) < 0.4) {
      tertiary.push(findEq('gac_contactor'));
    }
    if (isSaline) {
      if (p.conductivity > 10000 || p.tds > 8000) {
        tertiary.push(findEq('ro_system'));
        // Forward Osmosis for very high salinity where RO energy is prohibitive
        if (p.conductivity > 20000 || p.tds > 15000) {
          tertiary.push(findEq('forward_osmosis'));
        }
      } else if (p.conductivity > 5000) {
        tertiary.push(findEq('uf_system'));
      }
      if (p.tds > 50000) {
        tertiary.push(findEq('evap_mvc'));
      }
      // Membrane Distillation for saline + organics (proven pairing)
      if (p.conductivity > 15000 && (p.phenol > 2 || current.bod5 > 200)) {
        tertiary.push(findEq('membrane_distillation'));
      }
    }
    if ((current.bod5 / current.cod) < 0.3 || p.phenol > 10 || p.color > 100) {
      tertiary.push(findEq('aop_uv_h2o2'));
    }
    if (p.lead > 0.05 || p.arsenic > 0.03 || p.nickel > 0.2 || p.ammoniaN > 25) {
      tertiary.push(findEq('ion_exchange'));
    }
    if (tertiary.length > 0) {
      // Add backwash water tank when filters are present
      tertiary.push(findEq('backwash_tank'));
      applyStage(4, 'Tertiary', tertiary.filter(compatible), []);
    }

    // Polishing
    const polishing: EquipmentItem[] = [
      findEq('uv_disinfection'),
      p.flowRate < 5000 ? findEq('cartridge_filter') : findEq('bag_filter'),
    ];
    if (current.color > 30 || p.phenol > 2) {
      polishing.push(findEq('ozone_polish'));
    }
    // Add effluent polishing/contact tank for final settling and sampling
    polishing.push(findEq('effluent_polishing_tank'));
    applyStage(5, 'Polishing', polishing.filter(compatible), []);

    // Sludge
    const sludge: EquipmentItem[] = [
      findEq('gravity_thickener'),
      p.flowRate < 5000 ? findEq('screw_press') : findEq('belt_press'),
    ];
    if (p.tss > 500 || p.flowRate > 10000) {
      sludge.push(findEq('centrifuge'));
    }
    if (p.bod5 > 1000) {
      sludge.push(findEq('digester_anaerobic'));
    } else {
      sludge.push(findEq('digester_aerobic'));
    }
    if (p.flowRate < 2000) {
      sludge.push(findEq('drying_bed'));
    } else {
      sludge.push(findEq('thermal_dryer'));
    }
    if (p.tss > 1000) {
      sludge.push(findEq('plate_press'));
    }
    // Add sludge holding tank for temporary storage before dewatering
    sludge.push(findEq('sludge_holding_tank'));
    applyStage(6, 'Sludge', sludge.filter(compatible), []);

    // Disinfection
    const disinfection: EquipmentItem[] = [
      findEq('chlorine_dioxide'),
      ...(p.flowRate < 5000 ? [findEq('uv_led')] : []),
    ];
    applyStage(7, 'Disinfection', disinfection.filter(compatible), []);

    const allTanks = stages.flatMap(s => s.tanks);
    const totalVolume = allTanks.reduce((sum, t) => sum + t.volumeM3, 0);

    // ─── Lamella Settlement Calculation ───────────────────────────
    const lamellaEq = stages.flatMap(s => s.equipment).find(e => e.id === 'lamella_clarifier');
    const lamellaSettlement = lamellaEq
      ? calculateLamellaSettlement(lamellaEq, p.flowRate, p.tss, p.bod5)
      : undefined;

    return {
      stages,
      totalBodReduction: cumulativeBodRemoved,
      totalCodReduction: cumulativeCodRemoved,
      totalTssReduction: cumulativeTssRemoved,
      finalPh: current.ph,
      totalTanks: allTanks,
      totalTankVolumeM3: totalVolume,
      totalTankVolumeLitres: totalVolume * 1000,
      equipmentCount: stages.reduce((sum, s) => sum + s.equipment.length, 0),
      lamellaSettlement,
    };
  }, [hasCalculated, blended, dosingConfig, brewery]);

  // ─── Assessment uses blended params ─────────────────────────────
  const result: AssessmentResult = useMemo(() => {
    // Use blended parameters for assessment
    const p = blended.params;

    const bodClass = classifyBOD(p.bod5);
    const codClass = classifyCOD(p.cod);
    const ratio = p.cod > 0 ? p.bod5 / p.cod : 0;
    const ratioClass = classifyBODCODRatio(ratio);
    const tssClass = classifyTSS(p.tss);
    const tempClass = classifyTemperature(p.temperature);
    const ogClass = classifyOG(p.oilGrease);
    const phClass = classifyPH(p.ph);
    const tdsClass = classifyTDS(p.tds);
    const condClass = classifyConductivity(p.conductivity);
    const nhClass = classifyAmmonia(p.ammoniaN);
    const tnClass = classifyTN(p.totalNitrogen);
    const tpClass = classifyTP(p.totalPhosphorus);
    const clClass = classifyChlorides(p.chlorides);

    const parameterClassification = [
      { parameter: 'BOD₅ (mg/L)', value: p.bod5, ...bodClass },
      { parameter: 'COD (mg/L)', value: p.cod, ...codClass },
      { parameter: 'BOD₅/COD Ratio', value: Number(ratio.toFixed(2)), ...ratioClass },
      { parameter: 'TSS (mg/L)', value: p.tss, ...tssClass },
      { parameter: 'Temperature (°C)', value: p.temperature, ...tempClass },
      { parameter: 'O&G (mg/L)', value: p.oilGrease, ...ogClass },
      { parameter: 'pH', value: p.ph, ...phClass },
      { parameter: 'TDS (mg/L)', value: p.tds, ...tdsClass },
      { parameter: 'Conductivity (µS/cm)', value: p.conductivity, ...condClass },
      { parameter: 'Ammonia-N (mg/L)', value: p.ammoniaN, ...nhClass },
      { parameter: 'Total Nitrogen (mg/L)', value: p.totalNitrogen, ...tnClass },
      { parameter: 'Total Phosphorus (mg/L)', value: p.totalPhosphorus, ...tpClass },
      { parameter: 'Chlorides (mg/L)', value: p.chlorides, ...clClass },
    ];

    const metals = [
      { name: 'Lead (Pb)', value: p.lead, threshold: 0.1 },
      { name: 'Mercury (Hg)', value: p.mercury, threshold: 0.005 },
      { name: 'Cadmium (Cd)', value: p.cadmium, threshold: 0.05 },
      { name: 'Chromium (Cr)', value: p.chromium, threshold: 0.05 },
      { name: 'Arsenic (As)', value: p.arsenic, threshold: 0.05 },
      { name: 'Nickel (Ni)', value: p.nickel, threshold: 0.5 },
      { name: 'Zinc (Zn)', value: p.zinc, threshold: 1 },
      { name: 'Copper (Cu)', value: p.copper, threshold: 0.5 },
    ];

    const toxicityAssessment = metals.map(m => ({
      toxicant: m.name, value: m.value, threshold: m.threshold,
      status: m.value > m.threshold ? 'TOXIC' : 'Safe',
      risk: m.value > m.threshold ? 'High Risk' : 'Low Risk',
    }));

    toxicityAssessment.push(
      { toxicant: 'Sulfides', value: p.sulfides, threshold: 5, status: p.sulfides > 5 ? 'TOXIC' : 'Safe', risk: p.sulfides > 5 ? 'High Risk' : 'Low Risk' },
      { toxicant: 'Phenol', value: p.phenol, threshold: 10, status: p.phenol > 10 ? 'TOXIC' : 'Safe', risk: p.phenol > 10 ? 'High Risk' : 'Low Risk' },
      { toxicant: 'Surfactants', value: p.surfactants, threshold: 15, status: p.surfactants > 15 ? 'TOXIC' : 'Safe', risk: p.surfactants > 15 ? 'High Risk' : 'Low Risk' }
    );

    const toxicParametersCount = toxicityAssessment.filter(t => t.status === 'TOXIC').length;

    // ─── Salinity ───────────────────────────────────────────────────
    const salinityPPT = calculateSalinityPPT(p.conductivity);
    const salinityProb = calculateSalineProbability(p.conductivity);
    const tdsCondRatio = p.conductivity > 0 ? p.tds / p.conductivity : 0;
    const ratioValid = tdsCondRatio >= 0.4 && tdsCondRatio <= 0.8;
    const confidence = ratioValid ? salinityProb.confidence : 'Moderate (TDS ratio inconsistent)';

    const salinityWarnings: string[] = [];
    if (p.conductivity > 5000) {
      salinityWarnings.push('Conductivity exceeds 5,000 µS/cm — biological treatment viability severely compromised');
      salinityWarnings.push('MBBR, MBR, and activated sludge processes will fail or underperform');
    }
    if (p.conductivity > 15000) {
      salinityWarnings.push('High salinity (>15,000 µS/cm) — specialized saline-tolerant microbes or physicochemical treatment required');
    }
    if (p.tds > 5000) {
      salinityWarnings.push('TDS > 5,000 mg/L — osmotic stress will inhibit biological biomass');
    }
    if (tdsCondRatio < 0.4) {
      salinityWarnings.push('TDS/Conductivity ratio below 0.4 — unusual water chemistry, verify measurements');
    }

    const isSaline = p.conductivity > 5000 || p.tds > 5000;

    const salinity = {
      conductivity: p.conductivity,
      tds: p.tds,
      tdsConductivityRatio: Number(tdsCondRatio.toFixed(3)),
      estimatedSalinityPPT: Number(salinityPPT.toFixed(2)),
      estimatedSalinityMG: Math.round(salinityPPT * 1000),
      classification: salinityProb.label,
      probabilityPercent: salinityProb.probability,
      probabilityLabel: salinityProb.probability >= 90 ? 'Definite' : salinityProb.probability >= 50 ? 'Probable' : 'Unlikely',
      confidenceLevel: confidence,
      freshWaterProbability: salinityProb.freshProb,
      brackishProbability: salinityProb.brackishProb,
      salineProbability: salinityProb.salineProb,
      hypersalineProbability: salinityProb.hypersalineProb,
      warnings: salinityWarnings,
      biologicalImpact: isSaline
        ? 'HIGH IMPACT: Salinity >5,000 µS/cm causes osmotic stress, cell lysis, and nitrifier inhibition. Biological treatment (MBBR, MBR, AS, SBR) is NOT VIABLE without halophilic adaptation or dilution.'
        : 'Low impact: Conductivity within range suitable for standard biological treatment.',
      compatibleProcesses: isSaline
        ? ['RO / Membrane Filtration', 'Evaporation / Crystallization', 'Chemical Precipitation', 'DAF', 'Coagulation/Flocculation', 'AOP', 'Ion Exchange']
        : ['Activated Sludge', 'MBR', 'SBR', 'MBBR', 'Constructed Wetland', 'Extended Aeration'],
      incompatibleProcesses: isSaline
        ? ['MBBR (Moving Bed Biofilm Reactor)', 'MBR (Membrane Bioreactor)', 'Standard Activated Sludge', 'SBR (Sequencing Batch Reactor)', 'Trickling Filter', 'UASB (unless halophilic)']
        : [],
      recommendedTreatment: isSaline
        ? p.conductivity > 15000
          ? 'Primary: RO/UF + Evaporation | Secondary: Chemical Precipitation | Tertiary: Crystallization for Zero Liquid Discharge'
          : 'Primary: DAF + Coagulation/Flocculation | Secondary: RO/UF | Tertiary: Ion Exchange or AOP polishing'
        : 'Primary: Biological (AS/MBR/SBR) | Secondary: BNR | Tertiary: Cloth Filter or Activated Carbon',
    };

    // ─── Overall Treatability ───────────────────────────────────────
    const organicScore = (bodClass.score + codClass.score + ratioClass.score) / 3;
    const physicalScore = (tssClass.score + tempClass.score + ogClass.score) / 3;
    const chemicalScore = (phClass.score + tdsClass.score + clClass.score + (toxicParametersCount > 0 ? 2 : 1)) / 4;
    const salinityPenalty = isSaline ? 1.5 : 0;
    const overallTreatabilityIndex = Number(((organicScore + physicalScore + chemicalScore + salinityPenalty) / 3).toFixed(2));

    let treatabilityClassification: string;
    if (overallTreatabilityIndex <= 1.2) treatabilityClassification = 'Simple / Low Complexity';
    else if (overallTreatabilityIndex <= 1.8) treatabilityClassification = 'Moderate Complexity';
    else if (overallTreatabilityIndex <= 2.3) treatabilityClassification = 'High Complexity';
    else treatabilityClassification = 'Very High Complexity / Specialised';

    // ─── Pre-Treatment ────────────────────────────────────────────
    const preTreatmentRequirements = [
      { step: 'pH Neutralization', trigger: 'pH < 6 or pH > 9', status: p.ph < 6 || p.ph > 9 ? 'REQUIRED' : 'Not Required', inputValue: p.ph, priority: p.ph < 6 || p.ph > 9 ? 3 : 0 },
      { step: 'Oil/Grease Separation (API/DAF)', trigger: 'O&G > 15 mg/L', status: p.oilGrease > 15 ? 'REQUIRED' : 'Not Required', inputValue: p.oilGrease, priority: p.oilGrease > 15 ? 2 : 0 },
      { step: 'Primary Sedimentation', trigger: 'TSS > 200 mg/L', status: p.tss > 200 ? 'REQUIRED' : 'Not Required', inputValue: p.tss, priority: p.tss > 200 ? 1 : 0 },
      { step: 'Heavy Metals Precipitation', trigger: 'Any metal > threshold', status: toxicParametersCount > 0 ? 'REQUIRED' : 'Not Required', inputValue: toxicParametersCount, priority: toxicParametersCount > 0 ? 3 : 0 },
      { step: 'Flow Equalization', trigger: 'Flow variation > 2:1', status: 'Check plant design', inputValue: p.flowRate, priority: 1 },
      { step: 'Salinity Reduction', trigger: 'Conductivity > 5,000 µS/cm', status: isSaline ? 'REQUIRED' : 'Not Required', inputValue: p.conductivity, priority: isSaline ? 4 : 0 },
    ];

    // ─── Biological Viability ───────────────────────────────────
    const bioTests: AssessmentResult['biologicalViability'] = [
      { factor: 'Biodegradability (BOD/COD)', value: ratio.toFixed(2), criterion: '> 0.3 required', assessment: ratio > 0.3 ? 'Biodegradable' : 'Poorly Biodegradable', passFail: ratio > 0.3 ? 'PASS' : 'FAIL' },
      { factor: 'pH Suitability', value: p.ph, criterion: '6.0 – 9.0 acceptable', assessment: p.ph >= 6.0 && p.ph <= 9.0 ? 'Suitable' : 'Unsuitable', passFail: p.ph >= 6.0 && p.ph <= 9.0 ? 'PASS' : 'FAIL' },
      { factor: 'Temperature Suitability', value: p.temperature, criterion: '10 – 35°C acceptable', assessment: p.temperature >= 10 && p.temperature <= 35 ? 'Suitable' : 'Unsuitable', passFail: p.temperature >= 10 && p.temperature <= 35 ? 'PASS' : 'FAIL' },
      { factor: 'Toxicity Check', value: toxicParametersCount, criterion: '0 toxic metals preferred', assessment: toxicParametersCount === 0 ? 'No Toxicity' : 'Toxicity Present', passFail: toxicParametersCount === 0 ? 'PASS' : 'FAIL' },
      { factor: 'TDS Inhibition', value: p.tds, criterion: '< 5000 mg/L acceptable', assessment: p.tds < 5000 ? 'Low Inhibition' : 'High Inhibition', passFail: p.tds < 5000 ? 'PASS' : 'FAIL' },
      { factor: 'O&G Inhibition', value: p.oilGrease, criterion: '< 50 mg/L acceptable', assessment: p.oilGrease < 50 ? 'Low Inhibition' : 'High Inhibition', passFail: p.oilGrease < 50 ? 'PASS' : 'FAIL' },
      { factor: 'Salinity Check (Conductivity)', value: p.conductivity, criterion: '< 5000 µS/cm acceptable', assessment: p.conductivity < 5000 ? 'Low Salinity' : 'HIGH SALINITY', passFail: p.conductivity < 5000 ? 'PASS' : 'FAIL' },
    ];

    const passCount = bioTests.filter(t => t.passFail === 'PASS').length;
    const biologicalViabilityScore = `${passCount} / ${bioTests.length} tests passed`;
    const isViable = passCount >= 5 && !isSaline;
    const recommendedApproach = isViable
      ? toxicParametersCount > 0 || p.oilGrease > 15
        ? 'Biological + Polishing (Pre-treatment required)'
        : 'Biological Treatment (Primary)'
      : isSaline
        ? 'PHYSICOCHEMICAL ONLY (Saline water — biological processes NOT VIABLE)'
        : 'Physicochemical Treatment (Primary)';

    // ─── Process Compatibility ──────────────────────────────────
    const mkProc = (process: string, type: 'Biological' | 'Physicochemical', key: string) => {
      const score = computeScore(p, key, isSaline);
      const { reason, caveats, confidence } = computeReason(p, key, isSaline);
      return { process, type, score, compatibilityScore: score, compatibility: '', recommendation: '', reason, caveats, confidence };
    };

    const processCompatibility: AssessmentResult['processCompatibility'] = [
      mkProc('Activated Sludge (AS)',        'Biological',        'AS'),
      mkProc('Extended Aeration',             'Biological',        'EA'),
      mkProc('SBR',                           'Biological',        'SBR'),
      mkProc('MBR',                           'Biological',        'MBR'),
      mkProc('MBBR',                          'Biological',        'MBBR'),
      mkProc('Trickling Filter',              'Biological',        'TF'),
      mkProc('UASB',                          'Biological',        'UASB'),
      mkProc('Constructed Wetland',           'Biological',        'CW'),
      mkProc('Coagulation / Flocculation',    'Physicochemical',   'CF'),
      mkProc('DAF',                           'Physicochemical',   'DAF'),
      mkProc('Activated Carbon',              'Physicochemical',   'AC'),
      mkProc('Chemical Precipitation',        'Physicochemical',   'CP'),
      mkProc('AOP',                           'Physicochemical',   'AOP'),
      mkProc('Membrane Filtration (RO/UF)',   'Physicochemical',   'MF'),
      mkProc('Ion Exchange',                  'Physicochemical',   'IX'),
      mkProc('Evaporation / Crystallization', 'Physicochemical',   'EVAP'),
      mkProc('Nutrient Removal (BNR)',        'Biological',        'BNR'),
      mkProc('Cloth Filter / Disk Filter',    'Physicochemical',   'CLOTH'),
      mkProc('Multimedia Filter (MMF)',       'Physicochemical',   'MMF'),
      mkProc('Pressure Multimedia Filter',    'Physicochemical',   'PMF'),
      mkProc('GAC Adsorption',               'Physicochemical',   'GAC'),
    ];

    processCompatibility.forEach(proc => {
      if (proc.score >= 80) { proc.compatibility = 'High'; proc.recommendation = 'RECOMMENDED'; }
      else if (proc.score >= 60) { proc.compatibility = 'Moderate'; proc.recommendation = 'CONDITIONAL'; }
      else { proc.compatibility = 'Low'; proc.recommendation = 'NOT RECOMMENDED'; }
    });

    processCompatibility.sort((a, b) => b.score - a.score);

    // ─── Treatment Train ──────────────────────────────────────
    const treatmentTrain: AssessmentResult['treatmentTrain'] = [];
    const preSteps: string[] = [];
    if (p.ph < 6 || p.ph > 9) preSteps.push('pH Neutralization');
    if (p.oilGrease > 15) preSteps.push('DAF/API Separator');
    if (toxicParametersCount > 0) preSteps.push('Metals Precipitation');
    if (isSaline) preSteps.push('Salinity Reduction / Dilution');
    if (preSteps.length > 0) {
      treatmentTrain.push({ stage: 'Pre-Treatment', process: preSteps.join(' + '), scoreOrStatus: 'Required', purpose: 'Condition raw wastewater' });
    }

    if (isSaline) {
      const topPhysio = processCompatibility.filter(proc => proc.type === 'Physicochemical' && proc.recommendation === 'RECOMMENDED' && !proc.process.includes('Cloth') && !proc.process.includes('GAC'))[0];
      if (topPhysio) {
        treatmentTrain.push({ stage: 'Primary Treatment', process: topPhysio.process, scoreOrStatus: `${topPhysio.score}`, purpose: 'Main contaminant removal (physicochemical — saline safe)' });
      }
      if (p.tss > 100) {
        const mmf = processCompatibility.find(proc => proc.process === 'Multimedia Filter (MMF)' || proc.process === 'Pressure Multimedia Filter');
        if (mmf && mmf.recommendation === 'RECOMMENDED') {
          treatmentTrain.push({ stage: 'Filtration', process: mmf.process, scoreOrStatus: `${mmf.score}`, purpose: 'TSS/BOD reduction via depth filtration' });
        }
      }
      const secondary = processCompatibility.filter(proc => proc.type === 'Physicochemical' && proc.recommendation === 'RECOMMENDED' && proc.process !== treatmentTrain[treatmentTrain.length - 1]?.process && !proc.process.includes('GAC'))[0];
      if (secondary) {
        treatmentTrain.push({ stage: 'Secondary Treatment', process: secondary.process, scoreOrStatus: 'Required', purpose: 'Additional removal / salinity reduction' });
      }
      const gac = processCompatibility.find(proc => proc.process === 'GAC Adsorption');
      if (gac && gac.recommendation === 'RECOMMENDED' && (p.bod5 / p.cod < 0.4 || p.phenol > 5 || p.color > 75)) {
        treatmentTrain.push({ stage: 'Tertiary / Polishing', process: 'GAC Adsorption', scoreOrStatus: `${gac.score}`, purpose: 'Dissolved organics removal / BOD-COD polishing' });
      }
      if (p.conductivity > 10000 || p.tds > 8000) {
        treatmentTrain.push({ stage: 'Tertiary / Desalination', process: 'RO / UF + Evaporation', scoreOrStatus: 'Required', purpose: 'Salinity reduction / Zero Liquid Discharge' });
      }
    } else {
      const topBio = processCompatibility.filter(proc => proc.type === 'Biological' && proc.recommendation === 'RECOMMENDED')[0];
      const topPhysio = processCompatibility.filter(proc => proc.type === 'Physicochemical' && proc.recommendation === 'RECOMMENDED' && !proc.process.includes('Cloth') && !proc.process.includes('GAC'))[0];
      if (isViable && topBio) {
        treatmentTrain.push({ stage: 'Primary Treatment', process: topBio.process, scoreOrStatus: `${topBio.score}`, purpose: 'Main organic removal' });
      } else if (topPhysio) {
        treatmentTrain.push({ stage: 'Primary Treatment', process: topPhysio.process, scoreOrStatus: `${topPhysio.score}`, purpose: 'Main contaminant removal' });
      }
      if (p.tss > 100 && !treatmentTrain.some(t => t.process.includes('DAF') || t.process.includes('Coagulation'))) {
        const mmf = processCompatibility.find(proc => proc.process === 'Multimedia Filter (MMF)' || proc.process === 'Pressure Multimedia Filter');
        if (mmf && mmf.recommendation === 'RECOMMENDED') {
          treatmentTrain.push({ stage: 'Filtration', process: mmf.process, scoreOrStatus: `${mmf.score}`, purpose: 'TSS/BOD reduction via depth filtration' });
        }
      }
      const secondary = processCompatibility.filter(proc => proc.recommendation === 'RECOMMENDED' && proc.process !== treatmentTrain[treatmentTrain.length - 1]?.process && !proc.process.includes('GAC'))[0];
      if (secondary && (overallTreatabilityIndex > 1.5 || toxicParametersCount > 0)) {
        treatmentTrain.push({ stage: 'Secondary Treatment', process: secondary.process, scoreOrStatus: 'As needed', purpose: 'Additional/backup removal' });
      }
      const gac = processCompatibility.find(proc => proc.process === 'GAC Adsorption');
      if (gac && gac.recommendation === 'RECOMMENDED' && (p.bod5 / p.cod < 0.4 || p.phenol > 5 || p.color > 75)) {
        treatmentTrain.push({ stage: 'Tertiary / Polishing', process: 'GAC Adsorption', scoreOrStatus: `${gac.score}`, purpose: 'Dissolved organics removal / BOD-COD polishing' });
      }
      if (p.totalNitrogen > 15 || p.totalPhosphorus > 5) {
        treatmentTrain.push({ stage: 'Tertiary / Polishing', process: 'Nutrient Removal (BNR)', scoreOrStatus: 'As needed', purpose: 'Final polishing for discharge' });
      }
    }

    treatmentTrain.push({ stage: 'Sludge Management', process: 'Thickening + Dewatering', scoreOrStatus: 'Standard', purpose: 'Biomass/sludge handling' });

    // ─── Key Findings ─────────────────────────────────────────
    const keyFindings: AssessmentResult['keyFindings'] = [
      { metric: 'Overall Treatability Index', result: overallTreatabilityIndex.toString(), rating: overallTreatabilityIndex.toString(), implication: 'Difficulty level of treatment' },
      { metric: 'Biological Treatment Viability', result: isViable ? 'VIABLE' : isSaline ? 'NOT VIABLE (SALINE)' : 'NOT VIABLE', rating: biologicalViabilityScore, implication: 'Can biological processes be used?' },
      { metric: 'Toxic Compounds Detected', result: `${toxicParametersCount} caution${toxicParametersCount !== 1 ? 's' : ''}`, rating: toxicParametersCount > 0 ? 'Caution - Toxicity Present' : 'Safe', implication: 'Impact on biomass viability' },
      { metric: 'Pre-Treatment Required', result: `${preSteps.length} step${preSteps.length !== 1 ? 's' : ''}`, rating: preSteps.length > 0 ? 'YES' : 'NO', implication: 'Steps needed before main treatment' },
      { metric: 'Organic Load Level', result: bodClass.level, rating: bodClass.score.toString(), implication: 'BOD classification for process sizing' },
      { metric: 'Nutrient Removal Needed', result: p.totalNitrogen > 15 ? 'YES - Nitrification/Denitrification' : p.totalPhosphorus > 5 ? 'YES - P Removal' : 'No', rating: p.totalNitrogen > 15 || p.totalPhosphorus > 5 ? 'Required' : 'Optional', implication: 'Nutrient removal requirement' },
      { metric: 'Salinity Classification', result: salinityProb.label, rating: `${salinityProb.probability}% probability`, implication: isSaline ? 'Biological treatment NOT viable' : 'Standard processes applicable' },
    ];

    // ─── Cloth Filter ──────────────────────────────────────────
    const clothScore = computeScore(p, 'CLOTH', isSaline);
    let clothSuitability: string;
    let clothRecommendation: string;
    if (clothScore >= 85) { clothSuitability = 'Excellent'; clothRecommendation = 'Highly Recommended'; }
    else if (clothScore >= 70) { clothSuitability = 'Good'; clothRecommendation = 'Recommended'; }
    else if (clothScore >= 50) { clothSuitability = 'Fair'; clothRecommendation = 'Conditional'; }
    else { clothSuitability = 'Poor'; clothRecommendation = 'Not Recommended'; }

    const clothFilter = {
      suitabilityScore: clothScore, suitability: clothSuitability, recommendation: clothRecommendation,
      pros: [
        { aspect: 'Capital Cost', detail: 'Very low initial investment (50-70% less than MBR/AS); Minimal civil works; Modular design allows phased expansion' },
        { aspect: 'Operating Cost', detail: 'Extremely low energy (0.5-2 kWh/m3); No chemicals required; Minimal operator training; Low maintenance' },
        { aspect: 'Treatment Performance', detail: 'Excellent TSS removal (60-80%); Some BOD reduction (20-40% via solids); Clear effluent; Consistent quality' },
        { aspect: 'Footprint & Scale', detail: 'Very compact; Suitable for small-medium flows (<1000 m³/day); Easy retrofit to existing plants' },
        { aspect: 'Operational Simplicity', detail: 'Fully automatic operation possible; No complex process control; Handles flow variations well' },
        { aspect: 'Environmental Impact', detail: 'Low carbon footprint; No chemical residuals; Quiet operation; Minimal odor generation' },
      ],
      cons: [
        { aspect: 'Media Replacement', detail: 'Media replacement needed every 3-5 years; Frame corrosion possible in harsh environments' },
        { aspect: 'Backwash Handling', detail: 'Periodic cloth replacement cost; Backwash water requires handling (5-10% of throughput); Cleaning downtime' },
        { aspect: 'Performance Limits', detail: 'NOT a standalone BOD solution for high organic loads; No nutrient (N/P) removal; Cannot remove dissolved organics alone' },
        { aspect: 'Scale Constraints', detail: 'Not economical for very large flows (>5000 m³/day); Limited hydraulic capacity per unit' },
        { aspect: 'Operational Issues', detail: 'Cloth blinding/fouling with high O&G; Requires pre-screening (1-3 mm); Not suitable for high-fiber wastewater' },
        { aspect: 'Waste Management', detail: 'Backwash wastewater requires treatment; Used cloth is non-biodegradable waste; Sludge still requires disposal' },
      ],
      comparisons: [
        { technology: 'Sand Filter', advantage: 'Smaller footprint; Higher filtration rate; Self-cleaning backwash', whenOthersWin: 'Handles higher solids loading; Lower media replacement cost' },
        { technology: 'MBR', advantage: '80% lower capital cost; 90% lower energy; No membrane fouling/cleaning chemicals', whenOthersWin: 'MBR achieves >95% BOD and near-zero TSS; Produces reusable water' },
        { technology: 'DAF', advantage: 'No compressed air system needed; Lower energy; No chemical flocculation required', whenOthersWin: 'DAF better for oil/grease and very light particles; Higher hydraulic loading' },
        { technology: 'Coagulation + Sedimentation', advantage: 'No chemical dosing; No sludge from coagulants; Continuous operation', whenOthersWin: 'Better for colloidal particles and color; Handles very fine suspended solids' },
      ],
      verdict: clothScore >= 70
        ? 'Cloth filters are ideal for this wastewater profile. They excel at TSS removal and moderate BOD reduction. Best deployed AFTER biological treatment for polishing, or BEFORE membranes/UV for solids protection.'
        : clothScore >= 50
        ? 'Cloth filters may be suitable as a secondary or tertiary step, but should not be used as standalone primary treatment given the wastewater characteristics.'
        : 'Cloth filters are NOT recommended as primary treatment for this wastewater. Consider biological treatment or other physicochemical processes first.',
      bodReduction: p.bod5 < 500 ? '20-40% via solids removal' : '<20% - insufficient for high BOD',
      tssRemoval: p.tss >= 100 && p.tss <= 500 ? '60-80%' : p.tss > 500 ? '40-60%' : 'Limited benefit - low TSS',
      energyUse: '0.5 - 2 kWh/m3',
      idealFor: 'Small-to-medium flows (<1000 m³/day), TSS 100-500 mg/L, low O&G (<15 mg/L), as tertiary polishing or pre-membrane protection',
      notSuitableFor: 'High-BOD industrial (>500 mg/L) as standalone, high oil/grease (>50 mg/L), very large flows (>5000 m³/day), high-fiber wastewater',
    };

    // ─── Recommended Dosing ───────────────────────────────────
    const recommendedDosings: AssessmentResult['recommendedDosings'] = [];
    
    // Coagulant recommendation based on TSS/O&G
    if (p.tss > 150 || p.oilGrease > 15 || p.turbidity > 100) {
      const isSalineWater = isSaline;
      if (p.totalPhosphorus > 5) {
        recommendedDosings.push({ chemicalId: 'fecl3', name: 'Ferric Chloride', formula: 'FeCl3', recommendedDose: isSalineWater ? 200 : 150, doseUnit: 'mg/L', reason: 'High solids + phosphorus removal. Strong coagulant effective for TSS >150 mg/L and TP >5 mg/L.' });
      } else {
        recommendedDosings.push({ chemicalId: 'alum', name: 'Aluminium Sulfate', formula: 'Al2(SO4)3', recommendedDose: isSalineWater ? 150 : 100, doseUnit: 'mg/L', reason: 'High solids loading. Good all-round coagulant for TSS >150 mg/L or O&G >15 mg/L.' });
      }
      recommendedDosings.push({ chemicalId: 'polymer', name: 'Polyelectrolyte Flocculant', formula: 'Polymer', recommendedDose: 2, doseUnit: 'mg/L', reason: 'Required to bridge floc particles after coagulation. Essential for DAF performance.' });
    }
    
    // pH adjustment
    if (p.ph < 6.0) {
      recommendedDosings.push({ chemicalId: 'lime', name: 'Hydrated Lime', formula: 'Ca(OH)2', recommendedDose: Math.round((7.0 - p.ph) * 80), doseUnit: 'mg/L', reason: `pH ${p.ph.toFixed(1)} is too acidic. Lime raises pH to optimal 7.0-7.5 range for coagulation.` });
    } else if (p.ph > 8.5) {
      recommendedDosings.push({ chemicalId: 'caustic', name: 'Caustic Soda', formula: 'NaOH', recommendedDose: Math.round((p.ph - 7.5) * 50), doseUnit: 'mg/L', reason: `pH ${p.ph.toFixed(1)} is too alkaline. Caustic adjustment to bring pH into 7.0-7.5 optimal range.` });
    }
    
    // High TP without coagulant recommendation above
    if (p.totalPhosphorus > 5 && !recommendedDosings.some(d => d.chemicalId === 'fecl3')) {
      recommendedDosings.push({ chemicalId: 'fecl3', name: 'Ferric Chloride', formula: 'FeCl3', recommendedDose: isSaline ? 200 : 150, doseUnit: 'mg/L', reason: `High phosphorus (${p.totalPhosphorus} mg/L TP). Ferric chloride precipitates phosphate as FePO4.` });
    }
    
    // High colour / phenol
    if (p.color > 100 || p.phenol > 5) {
      recommendedDosings.push({ chemicalId: 'pac', name: 'Polyaluminium Chloride', formula: 'PAC', recommendedDose: 120, doseUnit: 'mg/L', reason: `High colour/phenol. PAC excellent for colour removal and organics coagulation.` });
    }
    
    // Chlorine for high bacteria / odour
    if (p.sulfides > 5 || p.ammoniaN > 30) {
      recommendedDosings.push({ chemicalId: 'chlorine', name: 'Sodium Hypochlorite', formula: 'NaOCl', recommendedDose: 10, doseUnit: 'mg/L', reason: 'High sulfides or ammonia. Oxidation for odour control and disinfection.' });
    }

    // ─── Dosing Warning ─────────────────────────────────────────
    // Check if user has already acknowledged dosing for this assessment
    const dosingAcknowledged = (() => {
      try {
        return sessionStorage.getItem('rb_dosing_acknowledged') === 'true';
      } catch { return false; }
    })();

    const activeDosings = dosingConfig.filter(d => d.enabled);
    const hasCoagulant = activeDosings.some(d => {
      const chemId = d.chemicalId;
      return ['fecl3', 'alum', 'pac', 'polymer'].includes(chemId);
    });
    const hasFlocculant = activeDosings.some(d => d.chemicalId === 'polymer');

    const affectedStages: string[] = [];
    if (simulation) {
      for (const stage of simulation.stages) {
        const stageName = stage.stageName.toLowerCase();
        if (stageName.includes('lamella') || stageName.includes('sediment') || stageName.includes('clarif')) {
          if (!hasCoagulant) affectedStages.push(stage.stageName);
        }
        if (stageName.includes('daf')) {
          if (!hasCoagulant || !hasFlocculant) affectedStages.push(stage.stageName);
        }
        if (stageName.includes('filter') && !hasCoagulant && p.tss > 100) {
          affectedStages.push(stage.stageName);
        }
      }
    }

    const dosingWarning: AssessmentResult['dosingWarning'] = (() => {
      // If dosing was acknowledged and we now have coagulant/flocculant, don't warn
      if (dosingAcknowledged && hasCoagulant && hasFlocculant) {
        return { show: false, message: '', severity: 'info' as const, affectedStages: [] };
      }
      if (activeDosings.length === 0) {
        return {
          show: !dosingAcknowledged, // hide if user explicitly dismissed
          message: 'No chemical dosing configured. Coagulation and flocculation chemicals are required for effective operation of DAF, Lamella Clarifier, and Multimedia Filters. Without dosing, TSS and O&G removal will be significantly reduced, and compliance may be affected.',
          severity: 'warning' as const,
          affectedStages: simulation?.stages.filter(s =>
            s.stageName.toLowerCase().includes('daf') ||
            s.stageName.toLowerCase().includes('lamella') ||
            s.stageName.toLowerCase().includes('clarif') ||
            s.stageName.toLowerCase().includes('filter')
          ).map(s => s.stageName) ?? [],
        };
      }
      if (affectedStages.length > 0 && !dosingAcknowledged) {
        const needsCoag = affectedStages.filter(s => s.toLowerCase().includes('lamella') || s.toLowerCase().includes('clarif') || s.toLowerCase().includes('daf'));
        const needsFloc = affectedStages.filter(s => s.toLowerCase().includes('daf'));
        let msg = '';
        if (needsCoag.length > 0 && !hasCoagulant) {
          msg += `Coagulant (e.g. Ferric Chloride, Aluminium Sulfate, or PAC) is required for: ${needsCoag.join(', ')}. `;
        }
        if (needsFloc.length > 0 && !hasFlocculant) {
          msg += `Polyelectrolyte flocculant is required for DAF to form settleable floc. `;
        }
        msg += 'Without proper dosing, solids removal efficiency will be significantly reduced and discharge compliance may fail.';
        return {
          show: true,
          message: msg,
          severity: 'warning' as const,
          affectedStages,
        };
      }
      return { show: false, message: '', severity: 'info' as const, affectedStages: [] };
    })();

    // ─── Equipment Dosing Requirements ───────────────────────────
    const equipmentDosingRequirements: AssessmentResult['equipmentDosingRequirements'] = simulation
      ? generateEquipmentDosingRequirements(simulation.stages, p)
      : [];

    return {
      parameterClassification, toxicityAssessment, toxicParametersCount,
      overallTreatabilityIndex, treatabilityClassification,
      preTreatmentRequirements, biologicalViability: bioTests,
      biologicalViabilityScore, recommendedApproach,
      processCompatibility, treatmentTrain, keyFindings,
      clothFilter, salinity, blended,
      simulation,
      dosingConfig,
      recommendedDosings,
      dosingWarning,
      equipmentDosingRequirements,
      equipmentValidation: (simulation?.stages ?? []).map(s => ({
        equipmentId: s.equipment.id,
        equipmentName: s.equipment.name,
        stage: s.stageName,
        stageNumber: s.stageNumber,
        triggerParam: s.triggerParam ?? 'Flow / Parameters',
        triggerValue: s.triggerValue ?? 'Assessment driven',
        threshold: s.threshold ?? 'Standard threshold',
        selected: true,
        passes: s.passes ?? true,
        principle: s.equipment.notes?.substring(0, 200) || '',
      })),
    };
  }, [blended, dosingConfig, simulation]);

  // ─── Sample Actions (after result so assessmentResult can be captured) ─

  const saveSample = useCallback((name: string) => {
    const newSample: SavedSample = {
      id: Date.now().toString(),
      name: name.trim() || `Sample ${samples.length + 1}`,
      date: new Date().toISOString(),
      params: { ...params },
      dairy: { ...dairy },
      brewery: { ...brewery },
      dosings: [...dosingConfig],
      assessmentResult: hasCalculated ? (result ?? null) : null,
    };
    setSamples(prev => [...prev, newSample]);
    setActiveSampleId(newSample.id);
    return newSample;
  }, [params, dairy, brewery, dosingConfig, samples.length, hasCalculated, result]);

  const loadSample = useCallback((id: string) => {
    const sample = samples.find(s => s.id === id);
    if (sample) {
      setParams(sample.params);
      if (sample.dairy) setDairy(sample.dairy);
      if (sample.brewery) setBrewery(sample.brewery);
      if (sample.dosings) setDosingConfig(sample.dosings);
      setActiveSampleId(id);
      setHasCalculated(!!sample.assessmentResult);
    }
  }, [samples]);

  const deleteSample = useCallback((id: string) => {
    setSamples(prev => prev.filter(s => s.id !== id));
    if (activeSampleId === id) setActiveSampleId(null);
  }, [activeSampleId]);

  // Acknowledge dosing (called when user applies dosing from popup)
  const acknowledgeDosing = useCallback(() => {
    try {
      sessionStorage.setItem('rb_dosing_acknowledged', 'true');
    } catch { /* ignore */ }
  }, []);

  // Restore last assessment from sessionStorage
  const restoreLastAssessment = useCallback(() => {
    try {
      const raw = sessionStorage.getItem('rb_last_assessment');
      if (!raw) return false;
      const data = JSON.parse(raw);
      if (data.params) setParams(data.params);
      if (data.dairy) setDairy(data.dairy);
      if (data.brewery) setBrewery(data.brewery);
      if (data.dosingConfig) setDosingConfig(data.dosingConfig);
      if (data.activeStandard) setActiveStandard(data.activeStandard);
      setHasCalculated(true);
      return true;
    } catch { return false; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    params, updateParam, resetParams, runAssessment, hasCalculated, result,
    dairy, updateDairy, brewery, updateBrewery,
    samples, saveSample, loadSample, deleteSample, activeSampleId,
    blended,
    dosingConfig, updateDosing, addDosing, addSpecificDosing, removeDosing,
    limits, updateLimit, resetLimits,
    activeStandard, updateStandard,
    acknowledgeDosing, restoreLastAssessment,
  };
}

// ─── Process Scoring Helper ─────────────────────────────────────────

function computeScore(p: WaterParameters, process: string, isSaline: boolean): number {
  let score = 70;
  const salinePenalty = isSaline ? 40 : 0;

  switch (process) {
    case 'AS':
      if (p.bod5 >= 100 && p.bod5 <= 500) score += 15;
      if (p.bod5 / p.cod > 0.3) score += 10;
      if (p.ph >= 6.5 && p.ph <= 8.5) score += 5;
      if (p.tds < 5000) score += 5;
      if (p.temperature >= 10 && p.temperature <= 35) score += 5;
      if (p.oilGrease > 50) score -= 15;
      if (p.lead > 0.1 || p.mercury > 0.005 || p.chromium > 0.05) score -= 10;
      score -= salinePenalty;
      break;
    case 'EA':
      if (p.bod5 < 300) score += 15;
      if (p.bod5 / p.cod > 0.3) score += 10;
      if (p.ph >= 6.5 && p.ph <= 8.5) score += 5;
      if (p.temperature >= 10 && p.temperature <= 35) score += 5;
      if (p.oilGrease > 50) score -= 15;
      score -= salinePenalty;
      break;
    case 'SBR':
      if (p.bod5 >= 100 && p.bod5 <= 500) score += 15;
      score += 5;
      if (p.bod5 / p.cod > 0.3) score += 10;
      if (p.ph >= 6.5 && p.ph <= 8.5) score += 5;
      if (p.totalNitrogen > 15) score += 5;
      if (p.oilGrease > 50) score -= 15;
      score -= salinePenalty;
      break;
    case 'MBR':
      if (p.bod5 >= 100 && p.bod5 <= 500) score += 15;
      if (p.bod5 / p.cod > 0.3) score += 10;
      if (p.tss < 200) score += 10;
      if (p.tds < 5000) score += 5;
      if (p.oilGrease > 50) score -= 20;
      score -= salinePenalty;
      if (isSaline) score -= 20;
      break;
    case 'MBBR':
      if (p.bod5 >= 100 && p.bod5 <= 500) score += 15;
      if (p.bod5 / p.cod > 0.3) score += 10;
      if (p.ph >= 6.5 && p.ph <= 8.5) score += 5;
      if (p.tds < 5000) score += 5;
      if (p.temperature >= 10 && p.temperature <= 35) score += 5;
      score -= salinePenalty * 1.2;
      if (isSaline) score -= 30;
      break;
    case 'TF':
      if (p.bod5 / p.cod > 0.5) score += 15;
      if (p.bod5 >= 100 && p.bod5 <= 400) score += 10;
      if (p.temperature >= 15 && p.temperature <= 30) score += 5;
      if (p.oilGrease > 50) score -= 15;
      score -= salinePenalty;
      break;
    case 'UASB':
      if (p.bod5 > 300) score += 20;
      if (p.temperature > 15) score += 10;
      if (p.bod5 / p.cod > 0.3) score += 5;
      if (p.tds < 5000) score += 5;
      if (p.temperature < 10) score -= 20;
      if (isSaline) score -= 25;
      break;
    case 'CW':
      if (p.flowRate < 100) score += 20;
      if (p.bod5 >= 50 && p.bod5 <= 300) score += 10;
      if (p.temperature >= 15 && p.temperature <= 35) score += 5;
      if (p.tds < 1500) score += 5;
      if (p.flowRate > 500) score -= 20;
      score -= salinePenalty;
      break;
    case 'CF':
      if (p.tss > 50 || p.color > 15) score += 20;
      if (p.turbidity > 5) score += 10;
      if (p.tds < 5000) score += 5;
      if (isSaline) score += 10;
      break;
    case 'DAF':
      if (p.oilGrease > 15 || p.tss > 100) score += 20;
      if (p.freeOil > 50) score += 10;
      if (p.tss > 200) score += 5;
      if (isSaline) score += 5;
      break;
    case 'AC':
      if (p.bod5 / p.cod < 0.3) score += 15;
      if (p.phenol > 5) score += 10;
      if (p.color > 75) score += 10;
      if (p.tds < 3000) score += 5;
      if (p.bod5 / p.cod > 0.5) score -= 15;
      if (isSaline) score += 5;
      break;
    case 'CP':
      if (p.lead > 0.05 || p.cadmium > 0.02 || p.chromium > 0.05 || p.arsenic > 0.03 || p.nickel > 0.2 || p.zinc > 0.5 || p.copper > 0.3) score += 25;
      if (p.mercury > 0.002) score += 10;
      if (isSaline) score += 5;
      break;
    case 'AOP':
      if (p.bod5 / p.cod < 0.3) score += 20;
      if (p.phenol > 10) score += 10;
      if (p.cod > 1000) score += 5;
      if (p.bod5 / p.cod > 0.5) score -= 20;
      if (isSaline) score += 5;
      break;
    case 'MF':
      if (p.tds > 1500) score += 20;
      if (p.tss < 50) score += 10;
      if (p.tds > 5000) score += 5;
      if (p.oilGrease > 15) score -= 15;
      if (isSaline) score += 25;
      break;
    case 'IX':
      if (p.lead > 0.05 || p.arsenic > 0.03 || p.nickel > 0.2) score += 20;
      if (p.ammoniaN > 25) score += 10;
      if (p.tds < 2000) score += 5;
      if (p.tds > 5000) score -= 15;
      if (isSaline) score -= 10;
      break;
    case 'EVAP':
      if (p.tds > 5000) score += 35;
      if (p.tds > 10000) score += 20;
      if (p.tds > 20000) score += 15;
      if (p.flowRate > 1000) score -= 15;
      if (isSaline) score += 20;
      break;
    case 'BNR':
      if (p.totalNitrogen > 15 || p.totalPhosphorus > 5) score += 25;
      if (p.ammoniaN > 5) score += 10;
      if (p.ph >= 6.5 && p.ph <= 8.5) score += 5;
      if (p.temperature >= 10 && p.temperature <= 35) score += 5;
      if (p.totalNitrogen < 10 && p.totalPhosphorus < 2) score -= 20;
      score -= salinePenalty;
      break;
    case 'CLOTH':
      if (p.tss >= 100 && p.tss <= 500) score += 20;
      if (p.flowRate < 1000) score += 10;
      if (p.oilGrease < 15) score += 10;
      if (p.bod5 < 500) score += 5;
      if (p.tss > 500 || p.oilGrease > 50) score -= 20;
      if (p.flowRate > 5000) score -= 15;
      if (isSaline) score += 5;
      break;
    case 'MMF':
      if (p.tss > 50) score += 25;
      if (p.tss > 200) score += 15;
      if (p.turbidity > 10) score += 10;
      if (p.bod5 >= 100 && p.bod5 <= 1000) score += 10;
      if (p.cod >= 200 && p.cod <= 2000) score += 5;
      if (p.oilGrease > 30) score -= 15;
      if (p.tss > 1000) score -= 10;
      if (isSaline) score += 5;
      break;
    case 'PMF':
      if (p.tss > 50) score += 25;
      if (p.flowRate > 500) score += 15;
      if (p.turbidity > 10) score += 10;
      if (p.bod5 >= 100 && p.bod5 <= 1000) score += 10;
      if (p.cod >= 200 && p.cod <= 2000) score += 5;
      if (p.oilGrease > 30) score -= 15;
      if (p.tss > 1000) score -= 10;
      if (p.flowRate < 100) score -= 10;
      if (isSaline) score += 5;
      break;
    case 'GAC':
      if (p.bod5 / p.cod < 0.4) score += 20;
      if (p.phenol > 5) score += 15;
      if (p.color > 75) score += 15;
      if (p.bod5 >= 50 && p.bod5 <= 500) score += 10;
      if (p.cod >= 100 && p.cod <= 1000) score += 10;
      if (p.tds > 5000) score -= 10;
      if (p.oilGrease > 50) score -= 20;
      if (p.surfactants > 15) score -= 10;
      if (isSaline) score -= 5;
      break;
  }

  return Math.max(15, Math.min(105, score));
}

function computeReason(p: WaterParameters, process: string, isSaline: boolean): { reason: string; caveats?: string; confidence?: string } {
  const bod = p.bod5; const cod = p.cod; const ratio = cod > 0 ? bod / cod : 0;
  const metals = p.lead > 0.1 || p.mercury > 0.005 || p.chromium > 0.05 || p.cadmium > 0.02 || p.arsenic > 0.03;

  switch (process) {
    case 'AS': {
      const parts: string[] = [];
      if (bod >= 100 && bod <= 500) parts.push(`BOD₅ ${bod} mg/L is ideal for AS (100–500 mg/L)`);
      else if (bod < 100) parts.push(`BOD₅ ${bod} mg/L is low for AS — substrate-limited`);
      else parts.push(`BOD₅ ${bod} mg/L is high — consider pre-treatment`);
      if (ratio > 0.3) parts.push(`BOD/COD ratio ${ratio.toFixed(2)} indicates good biodegradability`);
      else parts.push(`BOD/COD ratio ${ratio.toFixed(2)} suggests low biodegradability`);
      if (p.ph >= 6.5 && p.ph <= 8.5) parts.push(`pH ${p.ph} is within optimal range`);
      else parts.push(`pH ${p.ph} is outside optimal 6.5–8.5 range`);
      const caveats: string[] = [];
      if (p.oilGrease > 50) caveats.push(`Oil & grease ${p.oilGrease} mg/L will cause sludge bulking`);
      if (metals) caveats.push('Heavy metals detected — pre-treatment required');
      if (isSaline) caveats.push('High salinity inhibits nitrification');
      return { reason: parts.join('. '), caveats: caveats.join('; ') || undefined, confidence: p.tds < 5000 && ratio > 0.3 ? 'High' : 'Moderate' };
    }
    case 'EA': {
      const parts: string[] = [];
      if (bod < 300) parts.push(`BOD₅ ${bod} mg/L suits extended aeration (low-load)`);
      else parts.push(`BOD₅ ${bod} mg/L is high for extended aeration — excess oxygen demand`);
      if (ratio > 0.3) parts.push('Wastewater is biodegradable — good for aerobic treatment');
      const caveats = p.oilGrease > 50 ? 'Oil & grease will inhibit oxygen transfer' : undefined;
      return { reason: parts.join('. '), caveats, confidence: bod < 300 && ratio > 0.3 ? 'High' : 'Moderate' };
    }
    case 'SBR': {
      const parts: string[] = [`SBR excels at variable-load treatment`];
      if (p.totalNitrogen > 15) parts.push(`TN ${p.totalNitrogen} mg/L — SBR handles simultaneous nitrification/denitrification`);
      if (ratio > 0.3) parts.push(`BOD/COD ${ratio.toFixed(2)} confirms biodegradable influent`);
      if (p.oilGrease > 50) parts.push(`Oil & grease ${p.oilGrease} mg/L may cause foaming`);
      return { reason: parts.join('. '), confidence: 'High' };
    }
    case 'MBR': {
      const parts: string[] = [];
      if (p.tss < 200) parts.push(`TSS ${p.tss} mg/L is low — reduces membrane fouling risk`);
      else parts.push(`TSS ${p.tss} mg/L is elevated — high fouling potential`);
      if (ratio > 0.3) parts.push('Good biodegradability supports MBR biokinetics');
      if (p.oilGrease > 50) parts.push(`Oil & grease ${p.oilGrease} mg/L will foul membranes rapidly`);
      if (isSaline) parts.push('Salinity degrades membrane performance');
      return { reason: parts.join('. '), caveats: p.oilGrease > 20 ? 'Pre-screening for FOG essential' : undefined, confidence: p.tss < 200 && ratio > 0.3 && !isSaline ? 'High' : 'Low' };
    }
    case 'MBBR': {
      const parts: string[] = ['MBBR provides flexible biofilm surface'];
      if (bod >= 100 && bod <= 500) parts.push(`BOD₅ ${bod} mg/L is within optimal loading range`);
      if (ratio > 0.3) parts.push('Biodegradable substrate supports biofilm growth');
      if (isSaline) parts.push('Salinity significantly inhibits biofilm organisms');
      return { reason: parts.join('. '), confidence: !isSaline && ratio > 0.3 ? 'High' : 'Low' };
    }
    case 'TF': {
      const parts: string[] = [];
      if (ratio > 0.5) parts.push(`BOD/COD ${ratio.toFixed(2)} — highly biodegradable, ideal for trickling filters`);
      else parts.push(`BOD/COD ${ratio.toFixed(2)} — low ratio reduces TF efficiency`);
      if (bod >= 100 && bod <= 400) parts.push(`BOD₅ ${bod} mg/L is within TF capacity`);
      if (p.temperature >= 15 && p.temperature <= 30) parts.push(`Temperature ${p.temperature}°C is optimal`);
      if (p.oilGrease > 50) parts.push('High FOG will block filter media');
      return { reason: parts.join('. '), confidence: ratio > 0.5 ? 'High' : 'Moderate' };
    }
    case 'UASB': {
      const parts: string[] = [];
      if (bod > 300) parts.push(`BOD₅ ${bod} mg/L — high organic load suits anaerobic digestion`);
      else parts.push(`BOD₅ ${bod} mg/L — low organic load limits UASB methane yield`);
      if (p.temperature > 15) parts.push(`Temperature ${p.temperature}°C supports methanogenesis`);
      else parts.push(`Temperature ${p.temperature}°C is below optimal — reduced methanogen activity`);
      if (isSaline) parts.push('Salinity inhibits anaerobic consortia');
      return { reason: parts.join('. '), caveats: 'Requires granular sludge seeding; long start-up', confidence: bod > 300 && p.temperature > 15 && !isSaline ? 'High' : 'Low' };
    }
    case 'CW': {
      const parts: string[] = [];
      if (p.flowRate < 100) parts.push(`Flow ${p.flowRate} m³/d is well-suited to constructed wetlands`);
      else if (p.flowRate > 500) parts.push(`Flow ${p.flowRate} m³/d requires large land area — may not be practical`);
      if (p.temperature >= 15 && p.temperature <= 35) parts.push('Temperature supports wetland plant growth');
      if (p.tds < 1500) parts.push('Low TDS is compatible with wetland vegetation');
      return { reason: parts.join('. '), caveats: 'High land requirement; seasonal performance variation', confidence: p.flowRate < 100 ? 'Moderate' : 'Low' };
    }
    case 'CF': {
      const parts: string[] = [];
      if (p.tss > 50) parts.push(`TSS ${p.tss} mg/L — coagulation effective for suspended solids removal`);
      if (p.color > 15) parts.push(`Colour ${p.color} PCU — flocculation removes colloidal colour`);
      if (p.turbidity > 5) parts.push(`Turbidity ${p.turbidity} NTU elevated — responds well to coagulants`);
      if (isSaline) parts.push('Salinity slightly increases coagulant demand');
      return { reason: parts.join('. ') || 'Coagulation/flocculation applicable to clarify wastewater', confidence: p.tss > 50 || p.turbidity > 5 ? 'High' : 'Moderate' };
    }
    case 'DAF': {
      const parts: string[] = [];
      if (p.oilGrease > 15) parts.push(`Oil & grease ${p.oilGrease} mg/L — DAF is the primary FOG removal technology`);
      if (p.freeOil > 50) parts.push(`Free oil ${p.freeOil} mg/L — excellent DAF candidate`);
      if (p.tss > 100) parts.push(`TSS ${p.tss} mg/L — DAF floats and skims suspended solids`);
      if (isSaline) parts.push('Saline water supports bubble nucleation');
      return { reason: parts.join('. ') || 'DAF suitable for solids and FOG removal', confidence: p.oilGrease > 15 || p.tss > 100 ? 'High' : 'Moderate' };
    }
    case 'AC': {
      const parts: string[] = [];
      if (ratio < 0.3) parts.push(`BOD/COD ${ratio.toFixed(2)} — refractory organics present; adsorption required`);
      if (p.phenol > 5) parts.push(`Phenol ${p.phenol} mg/L — activated carbon removes phenolics effectively`);
      if (p.color > 75) parts.push(`Colour ${p.color} PCU — AC adsorbs colour compounds`);
      if (ratio > 0.5) parts.push(`BOD/COD ${ratio.toFixed(2)} — high biodegradability limits AC benefit`);
      return { reason: parts.join('. ') || 'Activated carbon targets trace organics', caveats: 'Regeneration costs are high for heavily loaded streams', confidence: ratio < 0.3 || p.phenol > 5 ? 'High' : 'Low' };
    }
    case 'CP': {
      const elevated: string[] = [];
      if (p.lead > 0.05) elevated.push(`Pb ${p.lead}`);
      if (p.cadmium > 0.02) elevated.push(`Cd ${p.cadmium}`);
      if (p.chromium > 0.05) elevated.push(`Cr ${p.chromium}`);
      if (p.arsenic > 0.03) elevated.push(`As ${p.arsenic}`);
      if (p.nickel > 0.2) elevated.push(`Ni ${p.nickel}`);
      if (p.zinc > 0.5) elevated.push(`Zn ${p.zinc}`);
      if (p.copper > 0.3) elevated.push(`Cu ${p.copper}`);
      if (p.mercury > 0.002) elevated.push(`Hg ${p.mercury}`);
      const reason = elevated.length > 0
        ? `Heavy metals elevated (${elevated.join(', ')} mg/L) — chemical precipitation converts dissolved metals to insoluble hydroxides`
        : 'No significant metal loading detected — chemical precipitation adds limited value';
      return { reason, caveats: 'pH adjustment required to pH 8.5–9.5 for optimal precipitation', confidence: elevated.length > 0 ? 'High' : 'Low' };
    }
    case 'AOP': {
      const parts: string[] = [];
      if (ratio < 0.3) parts.push(`BOD/COD ${ratio.toFixed(2)} — refractory compounds require oxidative destruction`);
      if (p.phenol > 10) parts.push(`Phenol ${p.phenol} mg/L — AOP (Fenton/UV/O₃) mineralises phenolics`);
      if (p.cod > 1000) parts.push(`COD ${p.cod} mg/L — high organic load warrants advanced oxidation`);
      if (ratio > 0.5) parts.push('High biodegradability — biological treatment is more cost-effective');
      return { reason: parts.join('. ') || 'AOP targets recalcitrant organics', caveats: 'High operating cost — best as polishing step', confidence: ratio < 0.3 && p.phenol > 5 ? 'High' : 'Low' };
    }
    case 'MF': {
      const parts: string[] = [];
      if (p.tds > 1500) parts.push(`TDS ${p.tds} mg/L — RO/NF removes dissolved salts`);
      if (p.tss < 50) parts.push(`TSS ${p.tss} mg/L is low — acceptable feed for membranes`);
      else parts.push(`TSS ${p.tss} mg/L requires pre-filtration to protect RO membranes`);
      if (isSaline) parts.push('Saline water is primary RO application');
      if (p.oilGrease > 15) parts.push(`FOG ${p.oilGrease} mg/L will foul membranes — DAF pre-treatment required`);
      return { reason: parts.join('. '), caveats: 'High CAPEX/OPEX; concentrate disposal required', confidence: p.tds > 1500 && p.tss < 50 ? 'High' : 'Moderate' };
    }
    case 'IX': {
      const parts: string[] = [];
      if (p.ammoniaN > 25) parts.push(`NH₃-N ${p.ammoniaN} mg/L — clinoptilolite IX removes ammonium selectively`);
      if (p.lead > 0.05 || p.arsenic > 0.03 || p.nickel > 0.2) parts.push('Heavy metals suit cation exchange resin');
      if (p.tds < 2000) parts.push(`TDS ${p.tds} mg/L is acceptable for IX`);
      else if (p.tds > 5000) parts.push(`TDS ${p.tds} mg/L saturates resin rapidly — high regenerant use`);
      if (isSaline) parts.push('Competing ions reduce selectivity');
      return { reason: parts.join('. ') || 'Ion exchange suitable for targeted ion removal', caveats: 'Resin regeneration produces concentrated brine waste', confidence: (p.ammoniaN > 25 || p.lead > 0.05) && p.tds < 2000 ? 'High' : 'Low' };
    }
    case 'EVAP': {
      const parts: string[] = [];
      if (p.tds > 5000) parts.push(`TDS ${p.tds} mg/L — evaporation achieves zero liquid discharge`);
      if (p.tds > 20000) parts.push('Ultra-high TDS — crystallisation to recover salts as solids');
      if (p.flowRate > 1000) parts.push(`Flow ${p.flowRate} m³/d is large — high energy cost for evaporation`);
      return { reason: parts.join('. ') || 'Evaporation concentrates dissolved solids', caveats: 'Very high energy cost; only viable for high-TDS, low-flow streams', confidence: p.tds > 5000 ? 'High' : 'Low' };
    }
    case 'BNR': {
      const parts: string[] = [];
      if (p.totalNitrogen > 15) parts.push(`TN ${p.totalNitrogen} mg/L — BNR removes nitrogen via nitrification/denitrification`);
      if (p.totalPhosphorus > 5) parts.push(`TP ${p.totalPhosphorus} mg/L — enhanced biological phosphorus removal applicable`);
      if (p.ammoniaN > 5) parts.push(`NH₃-N ${p.ammoniaN} mg/L — ammonia is primary nitrification substrate`);
      if (p.totalNitrogen < 10 && p.totalPhosphorus < 2) parts.push('Nutrient concentrations are low — BNR provides limited benefit');
      if (isSaline) parts.push('Salinity inhibits nitrifiers');
      return { reason: parts.join('. ') || 'BNR targets nitrogen and phosphorus removal', confidence: p.totalNitrogen > 15 || p.totalPhosphorus > 5 ? 'High' : 'Low' };
    }
    case 'CLOTH': {
      const parts: string[] = [];
      if (p.tss >= 100 && p.tss <= 500) parts.push(`TSS ${p.tss} mg/L — cloth/disk filters are optimised for this range`);
      else if (p.tss > 500) parts.push(`TSS ${p.tss} mg/L is too high — rapid blinding of cloth media`);
      if (p.oilGrease < 15) parts.push('Low FOG — cloth media will not blind prematurely');
      else parts.push(`FOG ${p.oilGrease} mg/L risks cloth blinding`);
      if (p.flowRate < 1000) parts.push(`Flow ${p.flowRate} m³/d within cloth filter capacity`);
      return { reason: parts.join('. '), confidence: p.tss >= 100 && p.tss <= 500 && p.oilGrease < 15 ? 'High' : 'Moderate' };
    }
    case 'MMF': {
      const parts: string[] = [];
      if (p.tss > 50) parts.push(`TSS ${p.tss} mg/L — multimedia filters remove suspended solids effectively`);
      if (p.turbidity > 10) parts.push(`Turbidity ${p.turbidity} NTU — MMF reduces turbidity via depth filtration`);
      if (p.oilGrease > 30) parts.push(`FOG ${p.oilGrease} mg/L will bind to media — backwash frequency increases`);
      if (p.tss > 1000) parts.push('TSS > 1000 mg/L overloads MMF — pre-treatment required');
      return { reason: parts.join('. ') || 'MMF provides robust suspended solids removal', confidence: p.tss > 50 && p.oilGrease < 30 ? 'High' : 'Moderate' };
    }
    case 'PMF': {
      const parts: string[] = [];
      if (p.tss > 50) parts.push(`TSS ${p.tss} mg/L — pressure drives faster filtration rates`);
      if (p.flowRate > 500) parts.push(`Flow ${p.flowRate} m³/d benefits from pressurised throughput`);
      else if (p.flowRate < 100) parts.push(`Low flow — pressure operation adds cost with little benefit`);
      if (p.oilGrease > 30) parts.push('FOG will foul pressure filter media');
      return { reason: parts.join('. '), confidence: p.tss > 50 && p.flowRate > 500 ? 'High' : 'Moderate' };
    }
    case 'GAC': {
      const parts: string[] = [];
      if (ratio < 0.4) parts.push(`BOD/COD ${ratio.toFixed(2)} — refractory organics suit GAC adsorption`);
      if (p.phenol > 5) parts.push(`Phenol ${p.phenol} mg/L — GAC has high affinity for phenolics`);
      if (p.color > 75) parts.push(`Colour ${p.color} PCU — GAC removes colour-causing compounds`);
      if (p.oilGrease > 50) parts.push(`FOG ${p.oilGrease} mg/L will saturate GAC rapidly`);
      if (p.surfactants > 15) parts.push(`Surfactants ${p.surfactants} mg/L reduce GAC adsorption capacity`);
      if (p.tds > 5000) parts.push('High TDS reduces GAC capacity via competing ions');
      return { reason: parts.join('. ') || 'GAC polishes trace organics and colour', caveats: 'Pre-filtration required to prevent GAC bed plugging', confidence: ratio < 0.4 && p.phenol > 0 && p.oilGrease < 50 ? 'High' : 'Moderate' };
    }
    default:
      return { reason: 'Score based on wastewater parameter compatibility with this process.' };
  }
}
